import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Paper,
  Typography,
  Stack,
  Box,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Collapse,
  Chip,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  FormHelperText,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
  Checkbox,
  FormGroup,
  Avatar,
  Badge,
  Autocomplete,
} from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar, PickersDay } from '@mui/x-date-pickers';

import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LocalHotelIcon from '@mui/icons-material/LocalHotel';
import HomeIcon from '@mui/icons-material/Home';
import HotelIcon from '@mui/icons-material/Hotel';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DownloadIcon from '@mui/icons-material/Download';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DrawIcon from '@mui/icons-material/Draw';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import UndoIcon from '@mui/icons-material/Undo';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PaymentsIcon from '@mui/icons-material/Payments';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import GavelIcon from '@mui/icons-material/Gavel';
import MapIcon from '@mui/icons-material/Map';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LanguageIcon from '@mui/icons-material/Language';
import CelebrationIcon from '@mui/icons-material/Celebration';
import SendIcon from '@mui/icons-material/Send';
import LinkIcon from '@mui/icons-material/Link';
import RoomMapModal, { CasaHotelMap, HotelBoutiqueMap } from './RoomMapModal';
import SignedCheckinsDialog from './SignedCheckinsDialog';
import { exportAssignment, exportAssignmentXlsx } from '../../../services/guestAssignmentService';
import { getPaymentStatus, createAssignment, updateAssignmentRoomGuest } from '../../../services/guestAssignmentService';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';


dayjs.extend(isBetween);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ─────────────────────────────────────────────
// FUNCIONES PARA MANEJO CORRECTO DE FECHAS
// ─────────────────────────────────────────────

function parseDate(dateStr) {
  if (!dateStr) return null;
  if (dayjs.isDayjs(dateStr)) return dateStr.startOf('day');
  if (dateStr instanceof Date) return dayjs(dateStr).local().startOf('day');
  if (typeof dateStr === 'string' && dateStr.includes('T')) {
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    return dayjs(new Date(year, month - 1, day)).startOf('day');
  }
  if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return dayjs(new Date(year, month - 1, day)).startOf('day');
  }
  const date = dayjs(dateStr);
  return date.isValid() ? date.startOf('day') : null;
}

function formatDateForDisplay(date) {
  const parsed = parseDate(date);
  return parsed ? parsed.format('DD/MM/YYYY') : '';
}

function toISODateOnly(date) {
  const parsed = parseDate(date);
  return parsed ? parsed.format('YYYY-MM-DD') : '';
}

// ─────────────────────────────────────────────
// COMPONENTE: Canvas de firma
// ─────────────────────────────────────────────

function SignatureCanvas({ canvasRef, onHasSignatureChange }) {
  const [isDrawing, setIsDrawing] = useState(false);

  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
    onHasSignatureChange(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoords(e);
    ctx.lineTo(coords.x, coords.y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onHasSignatureChange(false);
  };

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Paper
        elevation={3}
        sx={{
          display: 'inline-block',
          p: 1,
          border: '2px dashed',
          borderColor: 'primary.main',
          width: '100%',
          maxWidth: 500,
          touchAction: 'none',
        }}
      >
        <canvas
          ref={canvasRef}
          width={500}
          height={160}
          style={{
            border: '1px solid #ccc',
            backgroundColor: 'white',
            width: '100%',
            height: 'auto',
            maxWidth: '100%',
            touchAction: 'none',
            cursor: 'crosshair',
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </Paper>
      <Box sx={{ mt: 1 }}>
        <Button size="small" variant="outlined" startIcon={<ClearIcon />} onClick={clearCanvas}>
          Limpiar firma
        </Button>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────
// HELPERS: badges de origen de pago
// ─────────────────────────────────────────────

function getPaymentSourceInfo(r) {
  const req = r.specialRequests || '';

  // Canal humano tiene prioridad sobre el método de pago
  if (req.includes('WHATSAPP')) {
    return {
      label: 'WhatsApp',
      color: '#25D366',
      bg: '#f0fff4',
      border: '#25D36640',
      icon: <WhatsAppIcon sx={{ fontSize: 13 }} />,
      type: 'whatsapp',
    };
  }
  if (req.includes('TELEFONO')) {
    return {
      label: 'Teléfono',
      color: '#ff6d00',
      bg: '#fff8f0',
      border: '#ff6d0040',
      icon: <PhoneIcon sx={{ fontSize: 13 }} />,
      type: 'phone',
    };
  }
  if (req.includes('RECEPCION')) {
    return {
      label: 'Recepción',
      color: '#0277bd',
      bg: '#f0f8ff',
      border: '#0277bd40',
      icon: <LocalHotelIcon sx={{ fontSize: 13 }} />,
      type: 'reception',
    };
  }
  if (req.includes('BOOKING')) {
    return {
      label: 'Booking',
      color: '#6a1b9a',
      bg: '#fdf4ff',
      border: '#6a1b9a40',
      icon: <EventSeatIcon sx={{ fontSize: 13 }} />,
      type: 'booking',
    };
  }
  // Stripe = pagó en línea directamente
  if (r.stripePaymentIntentId) {
    return {
      label: 'Página web',
      color: '#6772e5',
      bg: '#f0f0ff',
      border: '#6772e540',
      icon: <LanguageIcon sx={{ fontSize: 13 }} />,
      type: 'stripe',
      badge: 'Stripe',
    };
  }
  // Fallback
  return {
    label: 'Página web',
    color: '#546e7a',
    bg: '#f5f5f5',
    border: '#546e7a30',
    icon: <LanguageIcon sx={{ fontSize: 13 }} />,
    type: 'web',
  };
}

// Pill de origen visual
function OriginPill({ reserva }) {
  const info = getPaymentSourceInfo(reserva);
  return (
    <Stack spacing={0.4} alignItems="flex-start">
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.3,
          borderRadius: 1.5,
          border: `1px solid ${info.border || info.color + '33'}`,
          bgcolor: info.bg,
          color: info.color,
          fontWeight: 700,
          fontSize: '0.7rem',
          whiteSpace: 'nowrap',
          letterSpacing: 0.1,
        }}
      >
        {info.icon}
        {info.label}
      </Box>
      {info.badge && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.4,
            px: 0.75,
            py: 0.15,
            borderRadius: 1,
            bgcolor: '#6772e510',
            color: '#6772e5',
            fontSize: '0.62rem',
            fontWeight: 600,
            border: '1px solid #6772e530',
          }}
        >
          <CreditCardIcon sx={{ fontSize: 10 }} />
          Stripe
        </Box>
      )}
    </Stack>
  );
}

// Pill de estado de reserva
function StatusPill({ status }) {
  const map = {
    active: { label: 'Activa', color: '#2e7d32', bg: '#f1fdf1' },
    cancelled: { label: 'Cancelada', color: '#c62828', bg: '#fff5f5' },
  };
  const s = map[status] || { label: status, color: '#546e7a', bg: '#f5f5f5' };
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: 99,
        border: `1px solid ${s.color}33`,
        bgcolor: s.bg,
        color: s.color,
        fontWeight: 600,
        fontSize: '0.7rem',
      }}
    >
      {s.label}
    </Box>
  );
}

// Pill de estado de pago
function PaymentPill({ paymentStatus, secondNightNotePaid }) {
  if (paymentStatus === 'completed') {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 1, py: 0.3, borderRadius: 1.5, border: '1px solid #2e7d3230', bgcolor: '#f1fdf1', color: '#2e7d32', fontWeight: 700, fontSize: '0.7rem' }}>
        <CheckCircleIcon sx={{ fontSize: 11 }} />
        Pagado
      </Box>
    );
  }
  if (paymentStatus === 'partial') {
    return (
      <Stack spacing={0.4} alignItems="flex-start">
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 1, py: 0.3, borderRadius: 1.5, border: '1px solid #e6510030', bgcolor: '#fff8f0', color: '#e65100', fontWeight: 700, fontSize: '0.7rem' }}>
          <PaymentsIcon sx={{ fontSize: 11 }} />
          Anticipo
        </Box>
        {secondNightNotePaid && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.15, borderRadius: 1, bgcolor: '#f1fdf1', color: '#2e7d32', fontSize: '0.62rem', fontWeight: 600, border: '1px solid #2e7d3230' }}>
            <CheckCircleIcon sx={{ fontSize: 9 }} />
            2da noche ✓
          </Box>
        )}
      </Stack>
    );
  }
  return (
    <Box sx={{ fontSize: '0.7rem', color: 'text.secondary', px: 1 }}>{paymentStatus}</Box>
  );
}

export function Reservas() {
  const [rooms, setRooms] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [resendingVoucherId, setResendingVoucherId] = useState(null);

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingDialogOpen, setEditingDialogOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);

  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const canvasPoliciesRef = useRef(null);
  const [hasSignaturePolicies, setHasSignaturePolicies] = useState(false);
  const [signatureDataURL, setSignatureDataURL] = useState(null);
  const [generatingCheckin, setGeneratingCheckin] = useState(false);

  const [checkinStep, setCheckinStep] = useState(1);
  const checkinSteps = ['Políticas del hotel', 'Datos Reserva', 'Confirmar Check-in'];

  const [additionalInfo, setAdditionalInfo] = useState({
    ciudad: '',
    estado: '',
    includeBreakfast: false,
    markSecondNightPaid: false,
  });

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    paymentStatus: '',
    dateRange: { start: null, end: null },
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [total, setTotal] = useState(0);
  const hasSearch = !!(filters.search || '').trim();

  const [precioManual, setPrecioManual] = useState('');
  const [anticipo, setAnticipo] = useState('');
  const [isFree, setIsFree] = useState(false);
  const [assignmentRooms, setAssignmentRooms] = useState([]);
  const [bookingItems, setBookingItems] = useState([{ roomId: '', fechaIn: '', fechaOut: '', precioManual: '', anticipo: '', isFree: false, manualPrice: '', specificRoomId: '' }]);

  const [openNew, setOpenNew] = useState(false);
  const [origin, setOrigin] = useState('RECEPCION');
  const [weddingDialogOpen, setWeddingDialogOpen] = useState(false);
  const [bookingContext, setBookingContext] = useState('general');
  const [weddingPaymentStatus, setWeddingPaymentStatus] = useState(null);
  const [weddingPaymentLoading, setWeddingPaymentLoading] = useState(false);
  const [createWeddingOpen, setCreateWeddingOpen] = useState(false);
  const [newWedding, setNewWedding] = useState({ eventName: '', brideName: '', brideEmail: '', bridePhone: '', groomEmail: '', hotelType: 'all', startDate: '', endDate: '' });
  const [creatingWedding, setCreatingWedding] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignBookings, setAssignBookings] = useState([]);
  const [assignAllRooms, setAssignAllRooms] = useState([]);
  const [assignRoomAssignments, setAssignRoomAssignments] = useState({});
  const [assignRoomSearchTerms, setAssignRoomSearchTerms] = useState({});
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [activeOrigin, setActiveOrigin] = useState('');
  const [clienteName, setClienteName] = useState('');
  const [clienteLastName, setClienteLastName] = useState('');
  const [clienteEmail, setClienteEmail] = useState('');
  const [clientePhone, setClientePhone] = useState('');
  const [activeTableSection, setActiveTableSection] = useState('all');
  const [weddingAssignments, setWeddingAssignments] = useState([]);
  const [selectedWeddingAssignmentId, setSelectedWeddingAssignmentId] = useState('');
  const [selectedWeddingAssignment, setSelectedWeddingAssignment] = useState(null);
  const [weddingGuestRooms, setWeddingGuestRooms] = useState([]);
  const [habitacionId, setHabitacionId] = useState('');
  const [fechaIn, setFechaIn] = useState('');
  const [fechaOut, setFechaOut] = useState('');
  const [useCalendar, setUseCalendar] = useState(false);
  const [range, setRange] = useState([null, null]);
  const [weddingCreateRoomId, setWeddingCreateRoomId] = useState(null);
  const [weddingCreateDates, setWeddingCreateDates] = useState({ startDate: '', endDate: '' });

  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [signedModalOpen, setSignedModalOpen] = useState(false);
  const [signedCheckinsCount, setSignedCheckinsCount] = useState(null);
  const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    Promise.all([
      fetch(`${API_URL}/api/rooms/admin`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
      fetch(`${API_URL}/api/bookings?page=1&pageSize=50`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
      fetch(`${API_URL}/api/room-blocks`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
      fetch(`${API_URL}/api/guest-assignments/admin/rooms`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
      fetch(`${API_URL}/api/bookings/signed-checkins`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
    ]).then(async ([roomsRes, bookingsRes, blocksRes, assignRoomsRes, signedRes]) => {
      if (!roomsRes.ok) throw new Error('Error al cargar habitaciones');
      const roomsData = await roomsRes.json();
      setRooms(roomsData);

      if (!bookingsRes.ok) throw new Error('Error al cargar reservas');
      const bookingsData = await bookingsRes.json();
      setReservas(Array.isArray(bookingsData.bookings) ? bookingsData.bookings : Array.isArray(bookingsData) ? bookingsData : []);
      setTotal(Number(bookingsData.total) || bookingsData.length || 0);

      if (!blocksRes.ok) throw new Error('Error al cargar bloqueos');
      const blocksData = await blocksRes.json();
      setBlocks(Array.isArray(blocksData) ? blocksData : []);

      if (!assignRoomsRes.ok) throw new Error('Error al cargar habitaciones específicas');
      const assignRoomsData = await assignRoomsRes.json();
      setAssignmentRooms(Array.isArray(assignRoomsData?.data) ? assignRoomsData.data : []);

      if (signedRes.ok) {
        const signedData = await signedRes.json();
        setSignedCheckinsCount(Array.isArray(signedData) ? signedData.length : null);
      }

      await loadWeddingAssignments();
    }).catch(err => {
      console.error('Error cargando datos iniciales:', err);
      setError(err.message || 'Error al cargar datos');
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (openNew) {
      const today = dayjs();
      const tomorrow = today.add(1, 'day');
      const defaultStart = weddingCreateDates.startDate || today.format('YYYY-MM-DD');
      const defaultEnd = weddingCreateDates.endDate || tomorrow.format('YYYY-MM-DD');
      const defaultRange = [dayjs(defaultStart), dayjs(defaultEnd)];

    if (weddingCreateRoomId) {
      const weddingRoom = assignAllRooms.find(r => String(r.roomId || r._id) === String(weddingCreateRoomId));
      const assignmentRoom = assignmentRooms.find(ar => String(ar.roomId) === String(weddingCreateRoomId));
      let matchedRoom = null;
      if (weddingRoom) {
        matchedRoom = rooms.find(r => r.type === weddingRoom.roomType?.type && r.lugar === weddingRoom.roomType?.lugar);
        if (!matchedRoom && assignmentRoom?.roomRefId) {
          matchedRoom = rooms.find(r => String(r._id) === String(assignmentRoom.roomRefId));
        }
      }
      const roomId = matchedRoom?._id || '';
      const specificRoomId = assignmentRoom?._id || '';
      setBookingItems([{ roomId, specificRoomId, fechaIn: defaultStart, fechaOut: defaultEnd, precioManual: '', anticipo: '', isFree: false, manualPrice: '' }]);
      setHabitacionId(roomId);
    }
      setFechaIn(defaultStart);
      setFechaOut(defaultEnd);
      setRange(defaultRange);
    }
  }, [openNew, weddingCreateRoomId, weddingCreateDates, assignAllRooms, assignmentRooms, rooms]);

  const lastFetchKeyRef = useRef('');

  useEffect(() => {
    const key = `${activeTableSection}|${activeOrigin}|${filters.status}|${filters.dateRange.start || ''}|${filters.dateRange.end || ''}|${(filters.search || '').trim()}`;
    if (lastFetchKeyRef.current !== key) {
      lastFetchKeyRef.current = key;
      setPage(1);
      fetchReservas(1, !!(filters.search || '').trim());
    }
  }, [activeTableSection, activeOrigin, filters.status, filters.dateRange.start, filters.dateRange.end, filters.search]);

  useEffect(() => {
    if (page !== 1) {
      fetchReservas(page, !!(filters.search || '').trim());
    }
  }, [page]);

  async function fetchSignedCheckinsCount() {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/signed-checkins`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) return;
      const data = await response.json();
      setSignedCheckinsCount(Array.isArray(data) ? data.length : null);
    } catch (err) {
      console.error('Error al cargar contador de check-ins firmados:', err);
    }
  }

  async function fetchRooms() {
    try {
      const response = await fetch(`${API_URL}/api/rooms/admin`);
      if (!response.ok) throw new Error('Error al cargar habitaciones');
      const data = await response.json();
      setRooms(data);
    } catch (err) {
      console.error('Error al cargar habitaciones:', err);
      setError('No se pudieron cargar las habitaciones');
    }
  }

  async function fetchReservas(pageNum = 1, forceSearchAll = false) {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const searchTerm = (filters.search || '').trim();
      const useSearchAll = forceSearchAll || !!searchTerm;

      const params = new URLSearchParams();
      if (searchTerm) {
        params.set('search', searchTerm);
      }
      if (filters.status) params.set('status', filters.status);
      if (activeOrigin) params.set('origin', activeOrigin);
      if (filters.dateRange.start) params.set('startDate', toISODateOnly(filters.dateRange.start));
      if (filters.dateRange.end) params.set('endDate', toISODateOnly(filters.dateRange.end));

      if (!useSearchAll) {
        params.set('page', String(pageNum));
        params.set('pageSize', String(pageSize));
      }

      const response = await fetch(`${API_URL}/api/bookings?${params.toString()}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error('Error al cargar reservas');
      const data = await response.json();
      if (useSearchAll) {
        setReservas(Array.isArray(data) ? data : []);
        setTotal(Array.isArray(data) ? data.length : 0);
      } else {
        setReservas(Array.isArray(data.bookings) ? data.bookings : []);
        setTotal(Number(data.total) || 0);
      }
      setPage(pageNum);
    } catch (err) {
      console.error('Error al cargar reservas:', err);
      setError('No se pudieron cargar las reservas');
    } finally {
      setLoading(false);
    }
  }

  async function fetchAllReservasForExport() {
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (activeOrigin) params.set('origin', activeOrigin);
      if (filters.dateRange.start) params.set('startDate', toISODateOnly(filters.dateRange.start));
      if (filters.dateRange.end) params.set('endDate', toISODateOnly(filters.dateRange.end));
      if (filters.search) params.set('search', (filters.search || '').trim());

      const response = await fetch(`${API_URL}/api/bookings?${params.toString()}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error('Error al cargar reservas para exportar');
      const data = await response.json();
      return Array.isArray(data) ? data : (Array.isArray(data.bookings) ? data.bookings : []);
    } catch (err) {
      console.error('Error al cargar reservas para exportar:', err);
      return [];
    }
  }

  async function fetchBlocks() {
    try {
      const response = await fetch(`${API_URL}/api/room-blocks`);
      if (!response.ok) throw new Error('Error al cargar bloqueos');
      const data = await response.json();
      setBlocks(data);
    } catch (err) {
      console.error('Error al cargar bloqueos:', err);
    }
  }

  async function fetchAssignmentRooms() {
    try {
      const response = await fetch(`${API_URL}/api/guest-assignments/admin/rooms`);
      if (!response.ok) throw new Error('Error al cargar habitaciones específicas');
      const data = await response.json();
      setAssignmentRooms(data?.data || []);
    } catch (err) {
      console.warn('No se pudieron cargar las habitaciones específicas:', err);
      setAssignmentRooms([]);
    }
  }

  function getOrigenLabel(r) {
    const req = (r.specialRequests || '').toUpperCase();
    if (req.includes('WHATSAPP')) return 'WhatsApp';
    if (req.includes('TELEFONO')) return 'Teléfono';
    if (req.includes('RECEPCION')) return 'Recepción';
    if (req.includes('BOOKING')) return 'Booking';
    if (r.stripePaymentIntentId) return 'Página web (Stripe)';
    return 'Página web';
  }

  function arrayBufferToBase64(buffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  // ─────────────────────────────────────────────
  // EXPORTAR HISTORIAL — Dashboard + Historial con diseño de marca
  // ─────────────────────────────────────────────

  async function handleExportHistory() {
    try {
      setLoading(true);
      let exportData = filteredReservas || [];
      const hasFilters = filters.search || filters.status || filters.paymentStatus || filters.dateRange.start || filters.dateRange.end || activeOrigin;

      if (!hasFilters && (!exportData || exportData.length === 0)) {
        exportData = await fetchAllReservasForExport();
      }

      const rows = (exportData || []).map(r => {
        const total = Number(r.totalPrice) || 0;
        const initial = Number(r.initialPayment) || 0;
        const second = Number(r.secondNightPayment) || 0;
        const isActive = r.status === 'active';
        return {
          bookingId: r.bookingId,
          guest: `${r.guestInfo?.firstName || ''} ${r.guestInfo?.lastName || ''}`.trim(),
          email: r.guestInfo?.email || '',
          phone: r.guestInfo?.phone || '',
          room: r.roomName || '',
          checkIn: formatDateForDisplay(r.checkIn),
          checkOut: formatDateForDisplay(r.checkOut),
          nights: r.nights || 0,
          total,
          initial,
          second,
          paymentStatus: r.paymentStatus === 'completed' ? 'Completado' : r.paymentStatus === 'partial' ? 'Parcial' : r.paymentStatus || '',
          origin: getOrigenLabel(r),
          status: r.status === 'active' ? 'Activa' : r.status === 'cancelled' ? 'Cancelada' : r.status,
          discount: r.discountCode || '',
          created: r.createdAt ? formatDateForDisplay(r.createdAt) : '',
          createdRaw: r.createdAt || null,
          revenue: isActive ? total : 0,
        };
      });

      // ── Agregados para el dashboard ──
      const totalIngresos = rows.reduce((sum, row) => sum + (row.status === 'Activa' ? row.total : 0), 0);
      const totalActivas = rows.filter(row => row.status === 'Activa').length;
      const totalCanceladas = rows.filter(row => row.status === 'Cancelada').length;
      const totalAnticipo = rows.reduce((sum, row) => sum + row.initial, 0);
      const totalPendiente = rows.reduce((sum, row) => sum + row.second, 0);
      const totalCompletado = rows.filter(row => row.paymentStatus === 'Completado').length;
      const totalParcial = rows.filter(row => row.paymentStatus === 'Parcial').length;

      const originMap = new Map();
      rows.forEach(row => {
        const key = row.origin || 'Otro';
        if (!originMap.has(key)) originMap.set(key, { count: 0, revenue: 0 });
        const entry = originMap.get(key);
        entry.count += 1;
        entry.revenue += row.revenue;
      });
      const originStats = Array.from(originMap.entries())
        .map(([label, v]) => ({ label, ...v }))
        .sort((a, b) => b.revenue - a.revenue);

      const monthMap = new Map();
      rows.forEach(row => {
        if (!row.createdRaw) return;
        const d = dayjs(row.createdRaw);
        if (!d.isValid()) return;
        const key = d.format('YYYY-MM');
        const label = d.format('MMM YYYY');
        if (!monthMap.has(key)) monthMap.set(key, { label, count: 0, revenue: 0 });
        const entry = monthMap.get(key);
        entry.count += 1;
        entry.revenue += row.revenue;
      });
      const monthStats = Array.from(monthMap.entries())
        .sort((a, b) => (a[0] > b[0] ? 1 : -1))
        .slice(-12)
        .map(([, v]) => v);

      const maxOriginRevenue = Math.max(1, ...originStats.map(o => o.revenue));
      const maxMonthRevenue = Math.max(1, ...monthStats.map(m => m.revenue));
      const maxPaymentCount = Math.max(1, totalCompletado, totalParcial);

      // ── Paleta de la marca La Capilla Hotel ──
      const GOLD = 'FFD4AF37';
      const DARK_GOLD = 'FF9C6F0E';
      const GOLD_SOFT = 'FFF6EFD9';
      const INK = 'FF1A1A1A';
      const GREEN = 'FF2E7D32';
      const GREEN_SOFT = 'FFEFF8EF';
      const RED = 'FFC62828';
      const RED_SOFT = 'FFFCEEEE';
      const BLUE = 'FF0277BD';
      const BLUE_SOFT = 'FFEDF6FB';
      const ORANGE = 'FFE65100';
      const GRAY_SOFT = 'FFF7F7F7';
      const WHITE = 'FFFFFFFF';
      const thinBorder = { style: 'thin', color: { argb: 'FFE0E0E0' } };
      const cellBorder = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'La Capilla Hotel';
      workbook.created = new Date();

      let logoBase64 = null;
      try {
        const logoResponse = await fetch('/logo512.png');
        if (logoResponse.ok) {
          const logoBlob = await logoResponse.blob();
          logoBase64 = arrayBufferToBase64(await logoBlob.arrayBuffer());
        }
      } catch (e) {
        console.warn('No se pudo cargar el logo:', e);
      }

      // ═══════════════════════════════════════════
      // HOJA 1 — DASHBOARD EJECUTIVO
      // ═══════════════════════════════════════════
      try {
        const dash = workbook.addWorksheet('Dashboard', {
          views: [{ showGridLines: false }],
          properties: { tabColor: { argb: GOLD } },
        });

        dash.getColumn(1).width = 17;
        dash.getColumn(2).width = 9;
        for (let c = 3; c <= 12; c++) dash.getColumn(c).width = 9.5;

        dash.getRow(1).height = 8;

        dash.mergeCells('A2:L3');
        const titleCell = dash.getCell('A2');
        titleCell.value = 'LA CAPILLA HOTEL';
        titleCell.font = { name: 'Georgia', bold: true, size: 26, color: { argb: INK } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

        dash.mergeCells('A4:L4');
        const subtitleCell = dash.getCell('A4');
        subtitleCell.value = 'Panel ejecutivo — Historial de reservas';
        subtitleCell.font = { name: 'Georgia', italic: true, size: 13, color: { argb: DARK_GOLD } };
        subtitleCell.alignment = { horizontal: 'center', vertical: 'middle' };

        dash.mergeCells('A5:L5');
        const dashDateCell = dash.getCell('A5');
        dashDateCell.value = `Generado el ${dayjs().format('DD/MM/YYYY')} a las ${dayjs().format('HH:mm')}`;
        dashDateCell.font = { italic: true, size: 10, color: { argb: 'FF888888' } };
        dashDateCell.alignment = { horizontal: 'center', vertical: 'middle' };

        if (logoBase64) {
          try {
            const imageId = workbook.addImage({ base64: logoBase64, extension: 'png' });
            dash.addImage(imageId, { tl: { col: 0.3, row: 0.3 }, ext: { width: 56, height: 56 } });
          } catch (e) { /* logo opcional */ }
        }

        dash.mergeCells('A6:L6');
        dash.getCell('A6').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GOLD } };
        dash.getRow(6).height = 3;

        const sectionTitle = (rowNum, text) => {
          dash.mergeCells(`A${rowNum}:L${rowNum}`);
          const cell = dash.getCell(`A${rowNum}`);
          cell.value = `  ${text}`;
          cell.font = { name: 'Georgia', bold: true, size: 12, color: { argb: WHITE } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: INK } };
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
          cell.border = { left: { style: 'medium', color: { argb: GOLD } } };
          dash.getRow(rowNum).height = 22;
        };

        sectionTitle(8, 'RESUMEN GENERAL');

        const kpis = [
          { label: 'Total reservas', value: rows.length, color: INK, soft: GRAY_SOFT, money: false },
          { label: 'Activas', value: totalActivas, color: GREEN, soft: GREEN_SOFT, money: false },
          { label: 'Canceladas', value: totalCanceladas, color: RED, soft: RED_SOFT, money: false },
          { label: 'Ingreso total', value: totalIngresos, color: DARK_GOLD, soft: GOLD_SOFT, money: true },
          { label: 'Anticipo recibido', value: totalAnticipo, color: BLUE, soft: BLUE_SOFT, money: true },
          { label: 'Saldo pendiente', value: totalPendiente, color: RED, soft: RED_SOFT, money: true },
        ];

        const kpiStartRow = 10;
        kpis.forEach((kpi, idx) => {
          const startCol = idx * 2 + 1;
          const endCol = startCol + 1;
          const startLetter = dash.getColumn(startCol).letter;
          const endLetter = dash.getColumn(endCol).letter;

          dash.mergeCells(`${startLetter}${kpiStartRow}:${endLetter}${kpiStartRow}`);
          dash.getCell(`${startLetter}${kpiStartRow}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: kpi.color } };
          dash.getRow(kpiStartRow).height = 4;

          dash.mergeCells(`${startLetter}${kpiStartRow + 1}:${endLetter}${kpiStartRow + 2}`);
          const valueCell = dash.getCell(`${startLetter}${kpiStartRow + 1}`);
          valueCell.value = kpi.value;
          valueCell.numFmt = kpi.money ? '"$"#,##0' : '#,##0';
          valueCell.font = { bold: true, size: 18, color: { argb: kpi.color } };
          valueCell.alignment = { horizontal: 'center', vertical: 'middle' };
          valueCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: kpi.soft } };

          dash.mergeCells(`${startLetter}${kpiStartRow + 3}:${endLetter}${kpiStartRow + 3}`);
          const labelCell = dash.getCell(`${startLetter}${kpiStartRow + 3}`);
          labelCell.value = kpi.label.toUpperCase();
          labelCell.font = { size: 8.5, bold: true, color: { argb: 'FF666666' } };
          labelCell.alignment = { horizontal: 'center', vertical: 'middle' };
          labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: kpi.soft } };

          [startLetter, endLetter].forEach((L) => {
            dash.getCell(`${L}${kpiStartRow + 1}`).border = { left: thinBorder, right: thinBorder };
            dash.getCell(`${L}${kpiStartRow + 3}`).border = { left: thinBorder, right: thinBorder, bottom: thinBorder };
          });
        });

        let cursorRow = kpiStartRow + 5;

        sectionTitle(cursorRow, 'ESTADO DE PAGOS');
        cursorRow += 2;
        const paymentRows = [
          { label: 'Pago completo', count: totalCompletado },
          { label: 'Con anticipo (parcial)', count: totalParcial },
        ];
        const paymentBarStartRow = cursorRow;
        paymentRows.forEach((p) => {
          dash.getCell(`A${cursorRow}`).value = p.label;
          dash.getCell(`A${cursorRow}`).font = { size: 10, bold: true };
          dash.mergeCells(`C${cursorRow}:K${cursorRow}`);
          dash.getCell(`C${cursorRow}`).value = p.count;
          dash.getCell(`L${cursorRow}`).value = p.count;
          dash.getCell(`L${cursorRow}`).font = { bold: true, size: 10 };
          dash.getCell(`L${cursorRow}`).alignment = { horizontal: 'right' };
          dash.getRow(cursorRow).height = 18;
          cursorRow += 1;
        });
        const paymentBarRange = `C${paymentBarStartRow}:K${cursorRow - 1}`;
        cursorRow += 1;

        sectionTitle(cursorRow, 'INGRESOS POR ORIGEN DE RESERVA');
        cursorRow += 2;
        const originHeaderRow = cursorRow;
        dash.getCell(`A${originHeaderRow}`).value = 'Origen';
        dash.getCell(`B${originHeaderRow}`).value = 'Reservas';
        dash.mergeCells(`D${originHeaderRow}:L${originHeaderRow}`);
        dash.getCell(`D${originHeaderRow}`).value = 'Ingreso';
        ['A', 'B', 'D'].forEach(L => {
          dash.getCell(`${L}${originHeaderRow}`).font = { bold: true, size: 9, color: { argb: 'FF888888' } };
        });
        cursorRow += 1;
        const originBarStartRow = cursorRow;
        originStats.forEach((o) => {
          dash.getCell(`A${cursorRow}`).value = o.label;
          dash.getCell(`A${cursorRow}`).font = { size: 10 };
          dash.getCell(`B${cursorRow}`).value = o.count;
          dash.getCell(`B${cursorRow}`).font = { size: 10 };
          dash.getCell(`B${cursorRow}`).alignment = { horizontal: 'center' };
          dash.mergeCells(`D${cursorRow}:L${cursorRow}`);
          dash.getCell(`D${cursorRow}`).value = o.revenue;
          dash.getCell(`D${cursorRow}`).numFmt = '"$"#,##0';
          dash.getCell(`D${cursorRow}`).font = { size: 9.5, bold: true, color: { argb: 'FF444444' } };
          dash.getRow(cursorRow).height = 17;
          cursorRow += 1;
        });
        const originBarRange = originStats.length ? `D${originBarStartRow}:L${cursorRow - 1}` : null;
        cursorRow += 1;

        sectionTitle(cursorRow, 'INGRESOS POR MES (ÚLTIMOS 12 CON DATOS)');
        cursorRow += 2;
        const monthHeaderRow = cursorRow;
        dash.getCell(`A${monthHeaderRow}`).value = 'Mes';
        dash.getCell(`B${monthHeaderRow}`).value = 'Reservas';
        dash.mergeCells(`D${monthHeaderRow}:L${monthHeaderRow}`);
        dash.getCell(`D${monthHeaderRow}`).value = 'Ingreso';
        ['A', 'B', 'D'].forEach(L => {
          dash.getCell(`${L}${monthHeaderRow}`).font = { bold: true, size: 9, color: { argb: 'FF888888' } };
        });
        cursorRow += 1;
        const monthBarStartRow = cursorRow;
        monthStats.forEach((m) => {
          dash.getCell(`A${cursorRow}`).value = m.label;
          dash.getCell(`A${cursorRow}`).font = { size: 10 };
          dash.getCell(`B${cursorRow}`).value = m.count;
          dash.getCell(`B${cursorRow}`).font = { size: 10 };
          dash.getCell(`B${cursorRow}`).alignment = { horizontal: 'center' };
          dash.mergeCells(`D${cursorRow}:L${cursorRow}`);
          dash.getCell(`D${cursorRow}`).value = m.revenue;
          dash.getCell(`D${cursorRow}`).numFmt = '"$"#,##0';
          dash.getCell(`D${cursorRow}`).font = { size: 9.5, bold: true, color: { argb: 'FF444444' } };
          dash.getRow(cursorRow).height = 17;
          cursorRow += 1;
        });
        const monthBarRange = monthStats.length ? `D${monthBarStartRow}:L${cursorRow - 1}` : null;

        try {
          dash.addConditionalFormatting({
            ref: paymentBarRange,
            rules: [{
              type: 'dataBar', priority: 1,
              cfvo: [{ type: 'num', value: 0 }, { type: 'num', value: maxPaymentCount }],
              color: { argb: GOLD }, gradient: true, border: false, showValue: false,
            }],
          });
          if (originBarRange) {
            dash.addConditionalFormatting({
              ref: originBarRange,
              rules: [{
                type: 'dataBar', priority: 1,
                cfvo: [{ type: 'num', value: 0 }, { type: 'num', value: maxOriginRevenue }],
                color: { argb: BLUE }, gradient: true, border: false, showValue: true,
              }],
            });
          }
          if (monthBarRange) {
            dash.addConditionalFormatting({
              ref: monthBarRange,
              rules: [{
                type: 'dataBar', priority: 1,
                cfvo: [{ type: 'num', value: 0 }, { type: 'num', value: maxMonthRevenue }],
                color: { argb: GREEN }, gradient: true, border: false, showValue: true,
              }],
            });
          }
        } catch (cfErr) {
          console.warn('No se pudieron aplicar las barras de datos del dashboard:', cfErr);
        }
      } catch (dashErr) {
        console.warn('No se pudo generar la hoja de Dashboard, se continúa solo con el historial:', dashErr);
      }

      // ═══════════════════════════════════════════
      // HOJA 2 — HISTORIAL DETALLADO
      // ═══════════════════════════════════════════
      const worksheet = workbook.addWorksheet('Historial', {
        views: [{ showGridLines: false }],
        pageSetup: { fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
        properties: { tabColor: { argb: GOLD } },
      });

      const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: INK } };
      const headerFont = { bold: true, color: { argb: WHITE }, size: 10.5 };
      const titleFont = { name: 'Georgia', bold: true, size: 18, color: { argb: INK } };
      const bannerFont = { bold: true, size: 12, color: { argb: WHITE } };
      const boldFont = { bold: true, size: 11 };

      worksheet.columns = [
        { header: 'ID Reserva', key: 'bookingId', width: 16 },
        { header: 'Huésped', key: 'guest', width: 26 },
        { header: 'Email', key: 'email', width: 30 },
        { header: 'Teléfono', key: 'phone', width: 15 },
        { header: 'Habitación', key: 'room', width: 20 },
        { header: 'Check-in', key: 'checkIn', width: 13 },
        { header: 'Check-out', key: 'checkOut', width: 13 },
        { header: 'Noches', key: 'nights', width: 9 },
        { header: 'Total', key: 'total', width: 13 },
        { header: 'Anticipo', key: 'initial', width: 13 },
        { header: 'Saldo pendiente', key: 'second', width: 15 },
        { header: 'Estado de pago', key: 'paymentStatus', width: 15 },
        { header: 'Origen', key: 'origin', width: 18 },
        { header: 'Estado', key: 'status', width: 12 },
        { header: 'Código descuento', key: 'discount', width: 15 },
        { header: 'Fecha creación', key: 'created', width: 15 },
        { header: 'Ingreso', key: 'revenue', width: 13 },
      ];

      worksheet.getRow(1).height = 8;

      worksheet.mergeCells('A2:Q3');
      const histTitle = worksheet.getCell('A2');
      histTitle.value = 'La Capilla Hotel — Historial de reservas';
      histTitle.font = titleFont;
      histTitle.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('A4:Q4');
      const histDate = worksheet.getCell('A4');
      histDate.value = `Generado: ${dayjs().format('DD/MM/YYYY HH:mm')} · ${rows.length} registros`;
      histDate.font = { italic: true, size: 10, color: { argb: 'FF666666' } };
      histDate.alignment = { horizontal: 'center', vertical: 'center' };
      worksheet.getRow(4).height = 18;

      worksheet.mergeCells('A5:Q5');
      worksheet.getCell('A5').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GOLD } };
      worksheet.getRow(5).height = 3;

      const summaryStartRow = 7;
      worksheet.mergeCells(`A${summaryStartRow}:Q${summaryStartRow}`);
      const summaryBanner = worksheet.getCell(`A${summaryStartRow}`);
      summaryBanner.value = '  RESUMEN';
      summaryBanner.font = bannerFont;
      summaryBanner.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: INK } };
      summaryBanner.alignment = { horizontal: 'left', vertical: 'center' };
      worksheet.getRow(summaryStartRow).height = 20;

      const summaryItems = [
        ['Total reservas', rows.length, false],
        ['Activas', totalActivas, false],
        ['Canceladas', totalCanceladas, false],
        ['Ingreso total', totalIngresos, true],
        ['Anticipo recibido', totalAnticipo, true],
        ['Saldo pendiente total', totalPendiente, true],
      ];

      summaryItems.forEach(([label, value, isMoney], idx) => {
        const r = summaryStartRow + 1 + idx;
        const rowFill = idx % 2 === 0 ? GRAY_SOFT : WHITE;

        worksheet.mergeCells(`A${r}:D${r}`);
        const labelCell = worksheet.getCell(`A${r}`);
        labelCell.value = label;
        labelCell.font = boldFont;
        labelCell.alignment = { horizontal: 'left', vertical: 'center' };
        labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowFill } };
        labelCell.border = cellBorder;

        worksheet.mergeCells(`E${r}:F${r}`);
        const valCell = worksheet.getCell(`E${r}`);
        valCell.value = value;
        if (isMoney) valCell.numFmt = '"$"#,##0.00';
        valCell.font = { bold: true, size: 11, color: { argb: isMoney ? DARK_GOLD : INK } };
        valCell.alignment = { horizontal: 'right', vertical: 'center' };
        valCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowFill } };
        valCell.border = cellBorder;
      });

      const headerRowIdx = summaryStartRow + 1 + summaryItems.length + 2;
      const dataHeaderRow = worksheet.getRow(headerRowIdx);
      dataHeaderRow.height = 26;
      worksheet.columns.forEach(col => {
        const cell = dataHeaderRow.getCell(col.key);
        if (cell) {
          cell.value = col.header;
          cell.font = headerFont;
          cell.fill = headerFill;
          cell.alignment = { horizontal: 'center', vertical: 'center', wrapText: true };
          cell.border = { ...cellBorder, top: { style: 'medium', color: { argb: GOLD } } };
        }
      });

      const moneyCols = ['total', 'initial', 'second', 'revenue'];
      const centerCols = ['bookingId', 'checkIn', 'checkOut', 'nights', 'paymentStatus', 'origin', 'status', 'discount', 'created'];

      rows.forEach((row, idx) => {
        const r = headerRowIdx + 1 + idx;
        const excelRow = worksheet.getRow(r);
        excelRow.height = 18;
        Object.keys(row).forEach(key => {
          if (key === 'createdRaw') return;
          const cell = excelRow.getCell(key);
          cell.value = row[key];
          cell.border = cellBorder;
          if (moneyCols.includes(key)) {
            cell.numFmt = '"$"#,##0.00';
            cell.alignment = { horizontal: 'right', vertical: 'center' };
          } else if (centerCols.includes(key)) {
            cell.alignment = { horizontal: 'center', vertical: 'center' };
          } else {
            cell.alignment = { horizontal: 'left', vertical: 'center' };
          }
        });
        if (idx % 2 === 0) {
          excelRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GRAY_SOFT } };
          });
        }
      });

      const lastDataRow = headerRowIdx + rows.length;

      const totalsRow = lastDataRow + 1;
      worksheet.mergeCells(`A${totalsRow}:H${totalsRow}`);
      const totalsLabelCell = worksheet.getCell(`A${totalsRow}`);
      totalsLabelCell.value = 'TOTALES (Ingreso considera solo reservas activas)';
      totalsLabelCell.font = { bold: true, italic: true, size: 9.5, color: { argb: 'FF666666' } };
      totalsLabelCell.alignment = { horizontal: 'right', vertical: 'center' };
      const totalColLetters = { total: 'I', initial: 'J', second: 'K' };
      ['total', 'initial', 'second'].forEach((key) => {
        const L = totalColLetters[key];
        const cell = worksheet.getRow(totalsRow).getCell(key);
        cell.value = { formula: `SUM(${L}${headerRowIdx + 1}:${L}${lastDataRow})` };
        cell.numFmt = '"$"#,##0.00';
        cell.font = { bold: true, size: 10.5 };
        cell.border = { top: { style: 'medium', color: { argb: GOLD } } };
        cell.alignment = { horizontal: 'right' };
      });
      worksheet.getRow(totalsRow).height = 20;

      try {
        worksheet.addConditionalFormatting({
          ref: `N${headerRowIdx + 1}:N${lastDataRow}`,
          rules: [
            { type: 'containsText', operator: 'containsText', text: 'Activa', priority: 1,
              style: { font: { color: { argb: GREEN }, bold: true }, fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: GREEN_SOFT } } } },
            { type: 'containsText', operator: 'containsText', text: 'Cancelada', priority: 2,
              style: { font: { color: { argb: RED }, bold: true }, fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: RED_SOFT } } } },
          ],
        });
        worksheet.addConditionalFormatting({
          ref: `L${headerRowIdx + 1}:L${lastDataRow}`,
          rules: [
            { type: 'containsText', operator: 'containsText', text: 'Completado', priority: 1,
              style: { font: { color: { argb: GREEN }, bold: true } } },
            { type: 'containsText', operator: 'containsText', text: 'Parcial', priority: 2,
              style: { font: { color: { argb: ORANGE }, bold: true } } },
          ],
        });
      } catch (cfErr) {
        console.warn('No se pudo aplicar formato condicional al historial:', cfErr);
      }

      worksheet.autoFilter = {
        from: { row: headerRowIdx, column: 1 },
        to: { row: lastDataRow, column: worksheet.columns.length },
      };
      worksheet.views = [{ showGridLines: false, state: 'frozen', ySplit: headerRowIdx }];
      workbook.views = [{ activeTab: 0 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `historial_reservas_${dayjs().format('DD-MM-YYYY')}.xlsx`;
      saveAs(blob, fileName);
      setSnack({ open: true, severity: 'success', message: `Historial exportado correctamente (${rows.length} registros)` });
    } catch (err) {
      console.error('Error exportando historial:', err);
      setSnack({ open: true, severity: 'error', message: 'No se pudo exportar el historial' });
    } finally {
      setLoading(false);
    }
  }

  async function loadWeddingAssignments(selectedId = null) {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/guest-assignments?limit=100&page=1`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error('Error al cargar bodas');
      const data = await response.json();
      const assignments = Array.isArray(data?.data) ? data.data : [];
      setWeddingAssignments(assignments);

      if (assignments.length > 0) {
        const selectedAssignment = selectedId
          ? assignments.find((assignment) => String(assignment._id) === String(selectedId))
          : assignments[0];

        if (selectedAssignment) {
          setSelectedWeddingAssignmentId(selectedAssignment._id);
          setSelectedWeddingAssignment(selectedAssignment);
          const allRooms = [...(selectedAssignment.casaHotelRooms || []), ...(selectedAssignment.boutiqueRooms || [])];
          const filteredRooms = allRooms.filter(room => room.guestName);
          setWeddingGuestRooms(filteredRooms);
          fetchWeddingPaymentStatus(selectedAssignment._id, filteredRooms);
          return;
        }
      }

      setSelectedWeddingAssignmentId('');
      setSelectedWeddingAssignment(null);
      setWeddingGuestRooms([]);
      setWeddingPaymentStatus(null);
    } catch (err) {
      console.error('Error loading wedding assignments:', err);
      setError(err.message || 'Error al cargar bodas');
    }
  }

  const normalizeGuestName = (name) => {
    return String(name || '').toLowerCase().replace(/[^a-z0-9ñáéíóúü\s]/gi, '').trim();
  };

  const mergeWeddingRoomsWithPayment = (rooms, paymentData) => {
    if (!Array.isArray(rooms)) return [];
    const paymentsByRoomId = new Map();
    const paymentsByName = new Map();
    (paymentData?.guests || []).forEach(g => {
      if (g.roomId) {
        paymentsByRoomId.set(String(g.roomId), g);
      }
      const normalized = normalizeGuestName(g.guestName);
      if (normalized) {
        paymentsByName.set(normalized, g);
      }
    });
    return rooms.map(room => {
      const normalizedRoomName = normalizeGuestName(room.guestName);
      let paymentEntry = paymentsByRoomId.get(String(room.roomId));
      if (!paymentEntry && normalizedRoomName) {
        paymentEntry = paymentsByName.get(normalizedRoomName);
      }
      return {
        ...room,
        hasPaid: Boolean(paymentEntry?.hasPaid),
        paymentStatus: paymentEntry?.booking?.paymentStatus || (paymentEntry?.hasPaid ? 'completed' : null),
        booking: paymentEntry?.booking || null,
        paymentTotal: paymentEntry?.booking?.totalPrice ?? null,
        paymentPaid: paymentEntry?.booking?.initialPayment ?? null,
        paymentPending: paymentEntry?.booking?.secondNightPayment ?? null,
      };
    });
  };

  const fetchWeddingPaymentStatus = async (assignmentId, roomsToMerge) => {
    if (!assignmentId) return;
    setWeddingPaymentLoading(true);
    try {
      const result = await getPaymentStatus(assignmentId);
      setWeddingPaymentStatus(result.data);
      if (roomsToMerge) {
        setWeddingGuestRooms(mergeWeddingRoomsWithPayment(roomsToMerge, result.data));
      } else {
        setWeddingGuestRooms(prev => mergeWeddingRoomsWithPayment(prev, result.data));
      }
    } catch (err) {
      console.error('Error al cargar estado de pagos de boda:', err);
      setWeddingPaymentStatus(null);
    } finally {
      setWeddingPaymentLoading(false);
    }
  };

  const getAssignmentRoomsForRoom = (roomId) => {
    if (!roomId || assignmentRooms.length === 0) return [];
    const selectedRoom = rooms.find(r => String(r._id) === String(roomId));
    if (!selectedRoom) return [];
    return assignmentRooms.filter(ar => {
      if (ar.roomRefId) return String(ar.roomRefId) === String(roomId);
      return ar.roomType?.type === selectedRoom.type && ar.roomType?.lugar === selectedRoom.lugar;
    });
  };

  const findAssignmentRoomPrice = (assignmentRoom) => {
    if (!assignmentRoom) return null;
    const matchedRoom = rooms.find(r =>
      r.type === assignmentRoom.roomType?.type &&
      r.lugar === assignmentRoom.roomType?.lugar
    );
    return matchedRoom ? matchedRoom.price : null;
  };

  const formatAssignmentRoomLabel = (assignmentRoom) => {
    if (!assignmentRoom) return '—';
    const label = assignmentRoom.roomId ? `${assignmentRoom.roomId} — ${assignmentRoom.name || ''}`.trim() : (assignmentRoom.name || '—');
    if (assignmentRoom.roomType?.type && assignmentRoom.roomType?.lugar) {
      const hotelLabel = assignmentRoom.roomType.lugar === 'casaHotel' ? 'Casa Hotel' : 'Boutique';
      return `${label} (${assignmentRoom.roomType.type.toLowerCase()} · ${hotelLabel})`;
    }
    return label;
  };

  const handleRoomTypeChange = (index, roomId) => {
    setBookingItems(prev => prev.map((item, idx) => idx === index ? { ...item, roomId, specificRoomId: '' } : item));
  };

  const handleWeddingSelection = (assignmentId) => {
    const selectedAssignment = weddingAssignments.find((assignment) => String(assignment._id) === String(assignmentId));
    setSelectedWeddingAssignmentId(assignmentId);
    setSelectedWeddingAssignment(selectedAssignment || null);
    if (selectedAssignment) {
      const allRooms = [...(selectedAssignment.casaHotelRooms || []), ...(selectedAssignment.boutiqueRooms || [])];
      const filteredRooms = allRooms.filter(room => room.guestName);
      setWeddingGuestRooms(filteredRooms);
      fetchWeddingPaymentStatus(assignmentId, filteredRooms);
    } else {
      setWeddingGuestRooms([]);
      setWeddingPaymentStatus(null);
    }
  };

  const handleCreateWeddingAssignment = async () => {
    if (!newWedding.eventName || !newWedding.brideName || !newWedding.brideEmail) {
      setSnack({ open: true, severity: 'error', message: 'Nombre del evento, email y nombre del contacto son requeridos.' });
      return;
    }
    setCreatingWedding(true);
    setError(null);
    try {
      const result = await createAssignment({ ...newWedding });
      const assignment = result.data;
      setSnack({ open: true, severity: 'success', message: `Boda "${assignment.eventName}" creada. Email enviado a ${assignment.brideEmail}.` });
      setCreateWeddingOpen(false);
      setNewWedding({ eventName: '', brideName: '', brideEmail: '', bridePhone: '', groomEmail: '', hotelType: 'all', startDate: '', endDate: '' });
      await loadWeddingAssignments(assignment._id);
    } catch (err) {
      console.error('Error creando boda:', err);
      setError(err.message || 'Error al crear la boda');
    } finally {
      setCreatingWedding(false);
    }
  };

  const openAssignDialog = async () => {
  if (!selectedWeddingAssignmentId) {
    setSnack({ open: true, severity: 'error', message: 'Selecciona una boda antes de asignar habitaciones.' });
    return;
  }
  setAssignError(null);
  setAssignDialogOpen(true);
  setAssignLoading(true);
  setAssignBookings([]);
  setAssignRoomAssignments({});
  setAssignRoomSearchTerms({});
  try {
    const token = localStorage.getItem('authToken');
    const assignment = selectedWeddingAssignment;
    const casaRooms = Array.isArray(assignment?.casaHotelRooms) ? assignment.casaHotelRooms : [];
    const boutiqueRooms = Array.isArray(assignment?.boutiqueRooms) ? assignment.boutiqueRooms : [];
    const allRooms = [...casaRooms, ...boutiqueRooms];
    setAssignAllRooms(allRooms);

    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (activeOrigin) params.set('origin', activeOrigin);
    if (filters.dateRange.start) params.set('startDate', toISODateOnly(filters.dateRange.start));
    if (filters.dateRange.end) params.set('endDate', toISODateOnly(filters.dateRange.end));
    params.set('page', '1');
    params.set('pageSize', '1000');

    const response = await fetch(`${API_URL}/api/bookings?${params.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!response.ok) throw new Error('No se pudieron cargar las reservas para asignar');
    const data = await response.json();
    const bookings = Array.isArray(data.bookings) ? data.bookings : Array.isArray(data) ? data : [];
    setAssignBookings(bookings);

    // 🆕 Reconstruir asignaciones existentes a partir de casaHotelRooms/boutiqueRooms
    // (fuente de verdad real, ya sincronizada con guestName), en vez de intentar
    // deducirlas desde booking.roomId (que apunta a la categoría de Room, no al roomId tipo "CH1").
    const normalizeGuestNameLocal = (name) =>
      String(name || '').toLowerCase().replace(/[^a-z0-9ñáéíóúü\s]/gi, '').trim();

    const preAssignments = {};
    const alreadyAssignedRooms = allRooms.filter(r => r.guestName && r.guestName.trim());

    alreadyAssignedRooms.forEach((r) => {
      const guestNameNorm = normalizeGuestNameLocal(r.guestName);

      const matchedBooking = bookings.find((booking) => {
        const bookingAssignmentId = booking.guestAssignmentId?._id || booking.guestAssignmentId || '';
        if (String(bookingAssignmentId) !== String(selectedWeddingAssignmentId)) return false;

        const bookingFullName = normalizeGuestNameLocal(
          `${booking.guestInfo?.firstName || ''} ${booking.guestInfo?.lastName || ''}`
        );
        return (
          bookingFullName === guestNameNorm ||
          bookingFullName.includes(guestNameNorm) ||
          guestNameNorm.includes(bookingFullName)
        );
      });

            if (matchedBooking) {
        preAssignments[r.roomId] = {
          bookingId: matchedBooking.bookingId,
          guestName: r.guestName,
          checkIn: matchedBooking.checkIn,
          checkOut: matchedBooking.checkOut,
          roomName: matchedBooking.roomName || '',
        };
      }
    });

    setAssignRoomAssignments(preAssignments);
  } catch (err) {
    console.error('Error cargando datos para asignar:', err);
    setAssignError(err.message || 'Error al cargar datos para asignar');
  } finally {
    setAssignLoading(false);
  }
};

  const updateAssignRoomSearch = (roomId, term) => {
    setAssignRoomSearchTerms(prev => ({ ...prev, [roomId]: term }));
  };

  const assignBookingToRoom = (roomId, booking) => {
    setAssignRoomAssignments(prev => ({
      ...prev,
      [roomId]: {
        bookingId: booking.bookingId,
        guestName: `${booking.guestInfo?.firstName || ''} ${booking.guestInfo?.lastName || ''}`.trim(),
        guestWhatsapp: booking.guestInfo?.phone || '',
        checkIn: booking.checkIn,
        checkOut: booking.checkOut,
        roomName: booking.roomName || '',
      }
    }));
    setAssignRoomSearchTerms(prev => ({ ...prev, [roomId]: '' }));
  };

  const unassignBookingFromRoom = (roomId) => {
    setAssignRoomAssignments(prev => {
      const next = { ...prev };
      delete next[roomId];
      return next;
    });
  };

  const openCreateBookingFromWedding = (room) => {
    const roomId = String(room.roomId || room._id);
    const startDate = selectedWeddingAssignment?.startDate ? dayjs(selectedWeddingAssignment.startDate).format('YYYY-MM-DD') : '';
    const endDate = selectedWeddingAssignment?.endDate ? dayjs(selectedWeddingAssignment.endDate).format('YYYY-MM-DD') : '';
    setWeddingCreateRoomId(roomId);
    setWeddingCreateDates({ startDate, endDate });
    setBookingContext('wedding');
    setOrigin('RECEPCION');
    setOpenNew(true);
  };

  const getAssignedBookingIds = () => {
    return Object.values(assignRoomAssignments).map(a => a.bookingId);
  };

const handleSaveBookingAssignments = async () => {
  const assignments = Object.entries(assignRoomAssignments);
  if (assignments.length === 0) {
    setAssignError('Asigna al menos una habitación a un huésped.');
    return;
  }
  setAssignSaving(true);
  setAssignError(null);
  try {
    const token = localStorage.getItem('authToken');
    const updates = assignments.map(async ([roomId, assignment]) => {
      const payload = { guestAssignmentId: selectedWeddingAssignmentId };

      const assignmentRoom = assignmentRooms.find(ar => String(ar.roomId) === String(roomId));
      let resolvedRoomId = assignmentRoom?.roomRefId || roomId;

      if (!assignmentRoom?.roomRefId && rooms.length > 0) {
        const roomName = assignAllRooms.find(r => String(r.roomId || r._id) === String(roomId))?.name || '';
        const matchedRoom = rooms.find(r =>
          r.name && roomName && (
            r.name.toLowerCase() === roomName.toLowerCase() ||
            r.name.toLowerCase().includes(roomName.toLowerCase()) ||
            roomName.toLowerCase().includes(r.name.toLowerCase())
          )
        );
        if (matchedRoom) {
          resolvedRoomId = matchedRoom._id;
        }
      }

      payload.roomId = resolvedRoomId;

      const selectedRoom = assignAllRooms.find(r => String(r.roomId || r._id) === String(roomId));
      if (selectedRoom) {
        payload.roomName = selectedRoom.name || selectedRoom.number || roomId;
      }

      const response = await fetch(`${API_URL}/api/bookings/${assignment.bookingId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.message || `Error actualizando ${assignment.bookingId}`);
      }
      const bookingResult = await response.json();

      // 🆕 Sincronizar el nombre del huésped en el array de la boda (casaHotelRooms/boutiqueRooms)
      try {
        await updateAssignmentRoomGuest(selectedWeddingAssignmentId, roomId, {
            guestName: assignment.guestName,
            guestWhatsapp: assignment.guestWhatsapp || '',
          });
      } catch (syncErr) {
        console.warn(`⚠️ No se pudo sincronizar el huésped en la boda para ${roomId}:`, syncErr.message);
        // No bloqueamos el flujo principal si esto falla; la reserva ya quedó ligada por guestAssignmentId
      }

      return bookingResult;
    });
    await Promise.all(updates);
    setAssignDialogOpen(false);
    await fetchReservas();
    if (selectedWeddingAssignmentId) {
      await loadWeddingAssignments(selectedWeddingAssignmentId);
    }
    setSnack({ open: true, severity: 'success', message: 'Asignaciones guardadas correctamente.' });
  } catch (err) {
    console.error('Error guardando asignaciones:', err);
    setAssignError(err.message || 'Error al guardar asignaciones');
  } finally {
    setAssignSaving(false);
  }
};

  const getRoomLabel = (room) => {
    if (!room) return '';
    return `${room.name} ${room.type ? `(${room.type}${room.lugar ? ` · ${room.lugar}` : ''})` : ''}`.trim();
  };

  // ─────────────────────────────────────────────
  // FILTRADO
  // ─────────────────────────────────────────────
  const filteredReservas = useMemo(() => {
    if (hasSearch) {
      return reservas;
    }
    return reservas.filter(reserva => {
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const rawBookingId = reserva.bookingId;
        const bookingId = (
          typeof rawBookingId === 'object'
            ? (rawBookingId?.$oid || rawBookingId?._id || '')
            : String(rawBookingId || '')
        ).toLowerCase();
        const gFirst = typeof reserva.guestInfo?.firstName === 'object'
          ? (reserva.guestInfo?.firstName?.$oid || '')
          : (reserva.guestInfo?.firstName || '');
        const gLast = typeof reserva.guestInfo?.lastName === 'object'
          ? (reserva.guestInfo?.lastName?.$oid || '')
          : (reserva.guestInfo?.lastName || '');
        const guestName = `${gFirst} ${gLast}`.toLowerCase();
        const gEmailRaw = reserva.guestInfo?.email;
        const guestEmail = (
          typeof gEmailRaw === 'object'
            ? (gEmailRaw?.$oid || gEmailRaw?._id || '')
            : String(gEmailRaw || '')
        ).toLowerCase();
        const rNameRaw = reserva.roomName;
        const roomName = (
          typeof rNameRaw === 'object'
            ? (rNameRaw?.$oid || rNameRaw?._id || '')
            : String(rNameRaw || '')
        ).toLowerCase();
        if (!bookingId.includes(searchTerm) && !guestName.includes(searchTerm) && !guestEmail.includes(searchTerm) && !roomName.includes(searchTerm)) {
          return false;
        }
      }

      if (filters.status && reserva.status !== filters.status) return false;
      if (filters.paymentStatus && reserva.paymentStatus !== filters.paymentStatus) return false;

      if (filters.dateRange.start || filters.dateRange.end) {
        const checkIn = parseDate(reserva.checkIn);
        if (!checkIn) return false;
        if (filters.dateRange.start) {
          const filterStart = parseDate(filters.dateRange.start);
          if (filterStart && checkIn.isBefore(filterStart, 'day')) return false;
        }
        if (filters.dateRange.end) {
          const filterEnd = parseDate(filters.dateRange.end);
          if (filterEnd && checkIn.isAfter(filterEnd, 'day')) return false;
        }
      }

      return true;
    });
  }, [reservas, filters, hasSearch]);

  const pagedReservas = useMemo(() => {
    if (hasSearch) return filteredReservas;
    return filteredReservas;
  }, [filteredReservas, hasSearch]);

  const clearAllFilters = () => {
    setFilters({ search: '', status: '', paymentStatus: '', dateRange: { start: null, end: null } });
    setActiveOrigin('');
  };

  const handleTabChange = (key) => {
    setActiveTableSection(key);
    if (key === 'all') {
      setActiveOrigin('');
    } else if (key === 'stripe') {
      setActiveOrigin('stripe');
    } else if (key === 'internal') {
      setActiveOrigin('internal');
    } else if (key === 'booking') {
      setActiveOrigin('booking');
    } else if (key === 'weddings') {
      setActiveOrigin('');
    } else if (key === 'cancelled') {
      setActiveOrigin('');
    }
  };

  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedWeddings, setExpandedWeddings] = useState({});

  function getReservaCategory(r) {
    if (r.discountCode || r.discountCodeId) return 'event';
    if (r.stripePaymentIntentId) return 'stripe';
    const req = (r.specialRequests || '').toUpperCase();
    if (req.includes('BOOKING')) return 'booking';
    return 'internal';
  }

  const visibleReservas = useMemo(() => {
    if (activeTableSection === 'all') return pagedReservas;
    if (activeTableSection === 'weddings') return pagedReservas;
    return pagedReservas.filter(r => getReservaCategory(r) === activeTableSection || (activeTableSection === 'cancelled' && r.status === 'cancelled'));
  }, [pagedReservas, activeTableSection]);

  function groupReservas(reservasList) {
    if (!reservasList || reservasList.length === 0) return [];
    const s = (v) => {
      if (v === null || v === undefined) return '';
      if (typeof v === 'object') return v?.$oid || v?.$date || JSON.stringify(v) || '';
      return String(v);
    };
    const groups = [];
    try {
      const normalized = reservasList
        .map(reserva => ({
          ...reserva,
          _bookingId: s(reserva.bookingId),
          _guestEmailKey: s(reserva.guestInfo?.email).toLowerCase(),
          _requestsKey: s(reserva.specialRequests || '').slice(0, 80),
          createdAtMs: reserva.createdAt
            ? (typeof reserva.createdAt === 'object' && reserva.createdAt.$date
              ? new Date(reserva.createdAt.$date).getTime()
              : new Date(reserva.createdAt).getTime())
            : 0,
        }))
          .sort((a, b) => {
            const dir = sortDir === 'asc' ? 1 : -1;
            if (sortBy === 'checkIn') {
              const ai = parseDate(a.checkIn); const bi = parseDate(b.checkIn);
              const av = ai ? ai.valueOf() : 0; const bv = bi ? bi.valueOf() : 0;
              if (av !== bv) return (av - bv) * dir;
            } else {
              if (a.createdAtMs !== b.createdAtMs) return (a.createdAtMs - b.createdAtMs) * dir;
            }
            return String(a._bookingId).localeCompare(String(b._bookingId)) * dir;
          });

      normalized.forEach((reserva) => {
        const key = `${reserva._guestEmailKey}|${reserva._requestsKey}|${Math.round(reserva.createdAtMs / 10000)}`;
        const lastGroup = groups[groups.length - 1];
        if (!lastGroup || lastGroup.key !== key) {
          groups.push({ key, createdAtMs: reserva.createdAtMs, guestEmail: reserva._guestEmailKey || 'Anónimo', items: [reserva] });
        } else {
          lastGroup.items.push(reserva);
        }
      });
    } catch (err) {
      console.error('[Reservas] groupReservas ERROR:', err);
    }
    return groups;
  }

  const visibleGroups = useMemo(() => groupReservas(visibleReservas), [visibleReservas, sortBy, sortDir]);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getWeddingBookings = (weddingId) => {
    return (reservas || []).filter(r => {
      const assignmentId = r.guestAssignmentId?._id || r.guestAssignmentId || '';
      return String(assignmentId) === String(weddingId);
    });
  };

  const toggleWedding = (weddingId) => {
    setExpandedWeddings((prev) => ({ ...prev, [weddingId]: !prev[weddingId] }));
  };

  async function handleExportWeddingHistory(wedding) {
    try {
      setLoading(true);
      const weddingBookings = getWeddingBookings(wedding._id);
      const exportData = weddingBookings.length > 0 ? weddingBookings : await fetchAllReservasForExport().then(data => data.filter(r => {
        const assignmentId = r.guestAssignmentId?._id || r.guestAssignmentId || '';
        return String(assignmentId) === String(wedding._id);
      }));

      const rows = (exportData || []).map(r => {
        const total = Number(r.totalPrice) || 0;
        const initial = Number(r.initialPayment) || 0;
        const second = Number(r.secondNightPayment) || 0;
        const isActive = r.status === 'active';
        return {
          bookingId: r.bookingId,
          guest: `${r.guestInfo?.firstName || ''} ${r.guestInfo?.lastName || ''}`.trim(),
          email: r.guestInfo?.email || '',
          phone: r.guestInfo?.phone || '',
          room: r.roomName || '',
          checkIn: formatDateForDisplay(r.checkIn),
          checkOut: formatDateForDisplay(r.checkOut),
          nights: r.nights || 0,
          total,
          initial,
          second,
          paymentStatus: r.paymentStatus === 'completed' ? 'Completado' : r.paymentStatus === 'partial' ? 'Parcial' : r.paymentStatus || '',
          origin: getOrigenLabel(r),
          status: r.status === 'active' ? 'Activa' : r.status === 'cancelled' ? 'Cancelada' : r.status,
          discount: r.discountCode || '',
          created: r.createdAt ? formatDateForDisplay(r.createdAt) : '',
          revenue: isActive ? total : 0,
        };
      });

      const totalIngresos = rows.reduce((sum, row) => sum + (row.status === 'Activa' ? row.total : 0), 0);
      const totalActivas = rows.filter(row => row.status === 'Activa').length;
      const totalCanceladas = rows.filter(row => row.status === 'Cancelada').length;
      const totalAnticipo = rows.reduce((sum, row) => sum + row.initial, 0);
      const totalPendiente = rows.reduce((sum, row) => sum + row.second, 0);

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'La Capilla Hotel';
      workbook.created = new Date();

      const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1a1a1a' } };
      const headerFont = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      const titleFont = { bold: true, size: 16, color: { argb: 'FF1a1a1a' } };
      const subtitleFont = { bold: true, size: 12, color: { argb: 'FF333333' } };
      const boldFont = { bold: true, size: 11 };
      const borderStyle = { style: 'thin', color: { argb: 'FFcccccc' } };
      const borders = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };

      const worksheet = workbook.addWorksheet('Historial', {
        pageSetup: { fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
      });

      worksheet.columns = [
        { header: 'ID Reserva', key: 'bookingId', width: 16 },
        { header: 'Huésped', key: 'guest', width: 28 },
        { header: 'Email', key: 'email', width: 32 },
        { header: 'Teléfono', key: 'phone', width: 16 },
        { header: 'Habitación', key: 'room', width: 22 },
        { header: 'Check-in', key: 'checkIn', width: 14 },
        { header: 'Check-out', key: 'checkOut', width: 14 },
        { header: 'Noches', key: 'nights', width: 10 },
        { header: 'Total', key: 'total', width: 14 },
        { header: 'Anticipo', key: 'initial', width: 14 },
        { header: 'Saldo pendiente', key: 'second', width: 16 },
        { header: 'Estado de pago', key: 'paymentStatus', width: 16 },
        { header: 'Origen', key: 'origin', width: 20 },
        { header: 'Estado', key: 'status', width: 14 },
        { header: 'Código descuento', key: 'discount', width: 16 },
        { header: 'Fecha creación', key: 'created', width: 16 },
        { header: 'Ingreso', key: 'revenue', width: 14 },
      ];

      worksheet.columns.forEach(col => {
        if (col.header) {
          col.header = { ...col.header, font: headerFont, fill: headerFill, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: borders };
        }
      });

      worksheet.getRow(1).height = 28;
      worksheet.mergeCells('A2:Q2');
      const titleRow = worksheet.getRow(2);
      titleRow.height = 32;
      titleRow.getCell(1).value = `La Capilla Hotel — ${wedding.eventName || 'Boda'} — Historial de reservas`;
      titleRow.getCell(1).font = titleFont;
      titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'center' };

      worksheet.mergeCells('A3:Q3');
      const dateRow = worksheet.getRow(3);
      dateRow.height = 20;
      dateRow.getCell(1).value = `Generado: ${dayjs().format('DD/MM/YYYY HH:mm')} · ${rows.length} registros`;
      dateRow.getCell(1).font = { italic: true, size: 10, color: { argb: 'FF666666' } };
      dateRow.getCell(1).alignment = { horizontal: 'center', vertical: 'center' };

      const summaryStartRow = 5;
      worksheet.mergeCells(`A${summaryStartRow}:B${summaryStartRow}`);
      worksheet.getCell(`A${summaryStartRow}`).value = 'RESUMEN';
      worksheet.getCell(`A${summaryStartRow}`).font = subtitleFont;
      worksheet.getCell(`A${summaryStartRow}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFf5f5f5' } };
      worksheet.getCell(`A${summaryStartRow}`).alignment = { horizontal: 'left', vertical: 'center' };
      worksheet.getCell(`A${summaryStartRow}`).border = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };

      const summaryItems = [
        ['Total reservas', rows.length, false],
        ['Activas', totalActivas, false],
        ['Canceladas', totalCanceladas, false],
        ['Ingreso total', totalIngresos, true],
        ['Anticipo recibido', totalAnticipo, true],
        ['Saldo pendiente total', totalPendiente, true],
      ];

      summaryItems.forEach(([label, value, isMoney], idx) => {
        const r = summaryStartRow + 1 + idx;
        worksheet.mergeCells(`A${r}:D${r}`);
        worksheet.getCell(`A${r}`).value = label;
        worksheet.getCell(`A${r}`).font = boldFont;
        worksheet.getCell(`A${r}`).alignment = { horizontal: 'left', vertical: 'center' };
        worksheet.getCell(`A${r}`).border = borders;

        worksheet.mergeCells(`E${r}:F${r}`);
        worksheet.getCell(`E${r}`).value = isMoney ? `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : String(value);
        worksheet.getCell(`E${r}`).font = { bold: true, size: 11 };
        worksheet.getCell(`E${r}`).alignment = { horizontal: 'right', vertical: 'center' };
        worksheet.getCell(`E${r}`).border = borders;
      });

      const dataStartRow = summaryStartRow + 1 + summaryItems.length + 1;
      const dataRow = worksheet.getRow(dataStartRow);
      dataRow.height = 26;
      worksheet.columns.forEach(col => {
        const cell = dataRow.getCell(col.key);
        if (cell) {
          cell.font = headerFont;
          cell.fill = headerFill;
          cell.alignment = { horizontal: 'center', vertical: 'center', wrapText: true };
          cell.border = borders;
        }
      });

      const moneyCols = ['total', 'initial', 'second', 'revenue'];
      const centerCols = ['bookingId', 'checkIn', 'checkOut', 'nights', 'paymentStatus', 'origin', 'status', 'discount', 'created'];

      rows.forEach((row, idx) => {
        const r = dataStartRow + 1 + idx;
        const excelRow = worksheet.getRow(r);
        excelRow.height = 18;
        Object.keys(row).forEach(key => {
          const cell = excelRow.getCell(key);
          cell.value = row[key];
          cell.border = borders;
          if (moneyCols.includes(key)) {
            cell.numFmt = '$#,##0.00';
            cell.alignment = { horizontal: 'right', vertical: 'center' };
          } else if (centerCols.includes(key)) {
            cell.alignment = { horizontal: 'center', vertical: 'center' };
          } else {
            cell.alignment = { horizontal: 'left', vertical: 'center' };
          }
        });
        if (idx % 2 === 0) {
          excelRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFf9f9f9' } };
          });
        }
      });

      worksheet.views = [{ showGridLines: false }];
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `boda_${wedding.eventName || wedding._id}_${dayjs().format('DD-MM-YYYY')}.xlsx`;
      saveAs(blob, fileName);
      setSnack({ open: true, severity: 'success', message: `Historial de boda exportado (${rows.length} registros)` });
    } catch (err) {
      console.error('Error exportando historial de boda:', err);
      setSnack({ open: true, severity: 'error', message: 'No se pudo exportar el historial de la boda' });
    } finally {
      setLoading(false);
    }
  }

  function renderWeddingsSection() {
    if (weddingAssignments.length === 0) {
      return (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Stack spacing={1} alignItems="center">
            <CelebrationIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">No hay bodas registradas</Typography>
            <Button variant="outlined" onClick={() => setCreateWeddingOpen(true)} startIcon={<AddCircleOutlineIcon />} size="small">Crear primera boda</Button>
          </Stack>
        </Paper>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setCreateWeddingOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Crear boda
          </Button>
        </Box>
        {weddingAssignments.map((wedding) => {
          const isExpanded = expandedWeddings[wedding._id];
          const weddingBookings = getWeddingBookings(wedding._id);
          const totalBoda = weddingBookings.reduce((sum, b) => sum + (Number(b.totalPrice) || 0), 0);
          const totalActivas = weddingBookings.filter(b => b.status === 'active').length;
          const totalCanceladas = weddingBookings.filter(b => b.status === 'cancelled').length;

          return (
            <Card key={wedding._id} variant="outlined" sx={{ borderRadius: 2, border: '1px solid #e0e0e0', transition: 'all 0.2s ease' }}>
              <Box sx={{ p: 2, cursor: 'pointer' }} onClick={() => toggleWedding(wedding._id)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" fontWeight="700" sx={{ fontFamily: 'Georgia, serif', color: '#1a1a1a', mb: 0.5 }}>
                      {wedding.eventName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {wedding.brideName} • {wedding.brideEmail}
                    </Typography>
                    {(wedding.startDate || wedding.endDate) && (
                      <Typography variant="caption" sx={{ display: 'inline-block', color: '#0277bd', fontWeight: 600, bgcolor: '#f5f5f5', px: 1, py: 0.3, borderRadius: 1, border: '1px solid #0277bd40' }}>
                        {formatDateForDisplay(wedding.startDate)} → {formatDateForDisplay(wedding.endDate)}
                      </Typography>
                    )}
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={`${weddingBookings.length} reservas`} size="small" sx={{ bgcolor: '#f5f5f5', color: '#666', border: '1px solid #e0e0e0' }} />
                      <Chip label={`${totalActivas} activas`} size="small" sx={{ bgcolor: '#f1fdf1', color: '#2e7d32', border: '1px solid #2e7d3230' }} />
                      <Chip label={`${totalCanceladas} canceladas`} size="small" sx={{ bgcolor: '#fff5f5', color: '#c62828', border: '1px solid #c6282830' }} />
                      <Chip label={`Total: $${totalBoda.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`} size="small" sx={{ bgcolor: '#fffbf0', color: '#d4af37', border: '1px solid #d4af37', fontWeight: 600 }} />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={(e) => { e.stopPropagation(); handleExportWeddingHistory(wedding); }}
                      sx={{ textTransform: 'none' }}
                    >
                      Exportar
                    </Button>
                    <IconButton size="small" sx={{ color: '#666' }}>
                      {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ p: 2, pt: 0, borderTop: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Button size="small" variant="contained" startIcon={<AssignmentIcon />} onClick={() => { setSelectedWeddingAssignmentId(wedding._id); openAssignDialog(); }}>
                      Asignar habitaciones
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={() => { /* copiar link */ }}>
                      Copiar link
                    </Button>
                  </Box>

                  {weddingBookings.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 1 }}>No hay reservas asignadas a esta boda aún.</Alert>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto', mt: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Reserva</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Huésped</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Habitación</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Fechas</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }} align="right">Total</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {weddingBookings.map((booking) => (
                            <TableRow key={booking.bookingId} hover sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                              <TableCell sx={{ width: 80, textAlign: 'center', py: 0.5 }}>
                                <Typography variant="caption" fontFamily="monospace" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                  {String(booking.bookingId).slice(-8).toUpperCase()}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ width: 140, py: 0.5 }}>
                                <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.72rem' }}>
                                  {booking.guestInfo?.firstName} {booking.guestInfo?.lastName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.62rem' }}>
                                  {booking.guestInfo?.email}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ width: 90, textAlign: 'center', py: 0.5 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>{booking.roomName}</Typography>
                              </TableCell>
                              <TableCell sx={{ width: 100, textAlign: 'center', py: 0.5 }}>
                                <Typography variant="caption" display="block" sx={{ fontSize: '0.68rem' }}>
                                  {formatDate(booking.checkIn)} → {formatDate(booking.checkOut)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
                                  {booking.nights}n
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ width: 75, textAlign: 'right', py: 0.5 }}>
                                <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.72rem' }}>
                                  {formatCurrency(booking.totalPrice)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ width: 70, textAlign: 'center', py: 0.5 }}>
                                <StatusPill status={booking.status} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </Collapse>
            </Card>
          );
        })}
      </Box>
    );
  }

  const nights = useMemo(() => {
    if (!fechaIn || !fechaOut) return 0;
    const inDate = fechaIn.split('-');
    const outDate = fechaOut.split('-');
    const checkIn = new Date(inDate[0], inDate[1] - 1, inDate[2]);
    const checkOut = new Date(outDate[0], outDate[1] - 1, outDate[2]);
    const diff = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  }, [fechaIn, fechaOut]);

  const unavailableDatesCache = useMemo(() => {
    const cache = {};
    if (!rooms.length || !reservas.length) return cache;

    const roomIdToLugar = {};
    rooms.forEach(r => { roomIdToLugar[String(r._id)] = r.lugar; });

    const byRoom = {};
    reservas.forEach(reserva => {
      if (reserva.status !== 'active') return;
      const resRoomId = reserva.roomId?._id || reserva.roomId;
      if (!resRoomId) return;
      const key = String(resRoomId);
      if (!byRoom[key]) byRoom[key] = [];
      byRoom[key].push(reserva);
    });

    const blocksByRoom = {};
    blocks.forEach(block => {
      if (!block.active) return;
      const scope = block.scope;
      if (scope === 'all') {
        rooms.forEach(r => {
          const key = String(r._id);
          if (!blocksByRoom[key]) blocksByRoom[key] = [];
          blocksByRoom[key].push(block);
        });
      } else if (scope === 'specific') {
        const blockRoomId = block.roomId?._id || block.roomId;
        if (blockRoomId) {
          const key = String(blockRoomId);
          if (!blocksByRoom[key]) blocksByRoom[key] = [];
          blocksByRoom[key].push(block);
        }
      } else if (scope === 'casaHotel' || scope === 'boutique') {
        rooms.forEach(r => {
          if (r.lugar === scope) {
            const key = String(r._id);
            if (!blocksByRoom[key]) blocksByRoom[key] = [];
            blocksByRoom[key].push(block);
          }
        });
      }
      if (block.affectedRooms && block.affectedRooms.length > 0) {
        block.affectedRooms.forEach(ar => {
          const aid = ar._id || ar;
          const key = String(aid);
          if (!blocksByRoom[key]) blocksByRoom[key] = [];
          blocksByRoom[key].push(block);
        });
      }
    });

    rooms.forEach(room => {
      const key = String(room._id);
      const unavailableDates = new Set();
      const roomBookings = byRoom[key] || [];
      roomBookings.forEach(reserva => {
        const start = parseDate(reserva.checkIn);
        const end = parseDate(reserva.checkOut);
        if (!start || !end) return;
        let current = start;
        while (current.isBefore(end, 'day')) {
          unavailableDates.add(current.format('YYYY-MM-DD'));
          current = current.add(1, 'day');
        }
      });
      const roomBlocks = blocksByRoom[key] || [];
      roomBlocks.forEach(block => {
        const blockStart = parseDate(block.startDate);
        const blockEnd = parseDate(block.endDate);
        if (!blockStart || !blockEnd) return;
        let current = blockStart;
        while (current.isBefore(blockEnd, 'day') || current.isSame(blockEnd, 'day')) {
          unavailableDates.add(current.format('YYYY-MM-DD'));
          current = current.add(1, 'day');
        }
      });
      cache[key] = Array.from(unavailableDates);
    });

    return cache;
  }, [rooms, blocks, reservas]);

  function isDayUnavailable(day, roomId) {
    if (!roomId) return false;
    const unavailableDates = unavailableDatesCache[roomId] || [];
    return unavailableDates.includes(day.format('YYYY-MM-DD'));
  }

  function isInRange(day, start, end) {
    if (!start || !end) return false;
    const dayNormalized = day.startOf('day');
    const startNormalized = parseDate(start)?.startOf('day');
    const endNormalized = parseDate(end)?.startOf('day');
    if (!startNormalized || !endNormalized) return false;
    return (dayNormalized.isSame(startNormalized) || dayNormalized.isAfter(startNormalized)) &&
      (dayNormalized.isSame(endNormalized) || dayNormalized.isBefore(endNormalized));
  }

  function isStartOrEnd(day, start, end) {
    if (!start || !end) return false;
    const dayNormalized = day.startOf('day');
    const startNormalized = parseDate(start)?.startOf('day');
    const endNormalized = parseDate(end)?.startOf('day');
    if (!startNormalized || !endNormalized) return false;
    return dayNormalized.isSame(startNormalized) || dayNormalized.isSame(endNormalized);
  }

  function handleCalendarDayClick(day) {
    const firstItem = bookingItems[0] || {};
    if (!firstItem.roomId) {
      setSnack({ open: true, severity: 'error', message: 'Seleccione una habitación primero' });
      return;
    }
    const roomId = firstItem.roomId;
    const dayNormalized = day.startOf('day');
    if (isDayUnavailable(dayNormalized, roomId)) {
      setSnack({ open: true, severity: 'error', message: 'Fecha no disponible' });
      return;
    }
    if (!range[0] || (range[0] && range[1])) {
      setRange([dayNormalized, null]);
      updateBookingItem(0, 'fechaIn', dayNormalized.format('YYYY-MM-DD'));
      updateBookingItem(0, 'fechaOut', '');
    } else {
      const start = range[0]?.startOf('day') || dayNormalized;
      if (dayNormalized.isBefore(start, 'day')) {
        setSnack({ open: true, severity: 'error', message: 'Check-out debe ser después del check-in' });
        return;
      }
      const unavailableDates = unavailableDatesCache[roomId] || [];
      let current = start.add(1, 'day');
      let hasUnavailable = false;
      while (current.isBefore(dayNormalized, 'day')) {
        if (unavailableDates.includes(current.format('YYYY-MM-DD'))) { hasUnavailable = true; break; }
        current = current.add(1, 'day');
      }
      if (hasUnavailable) {
        setSnack({ open: true, severity: 'error', message: 'Hay fechas no disponibles en el rango' });
        return;
      }
      setRange([start, dayNormalized]);
      updateBookingItem(0, 'fechaIn', start.format('YYYY-MM-DD'));
      updateBookingItem(0, 'fechaOut', dayNormalized.format('YYYY-MM-DD'));
    }
  }

  const checkAvailability = async (roomId, checkIn, checkOut) => {
    if (!roomId || !checkIn || !checkOut) return null;
    try {
      const response = await fetch(`${API_URL}/api/bookings/availability?roomId=${roomId}&checkIn=${checkIn}&checkOut=${checkOut}`);
      if (!response.ok) return null;
      return await response.json();
    } catch (err) {
      console.error('Error verificando disponibilidad:', err);
      return null;
    }
  };

  const [availabilityData, setAvailabilityData] = useState(null);

  useEffect(() => {
    const firstItem = bookingItems[0] || {};
    if (firstItem.roomId && firstItem.fechaIn && firstItem.fechaOut) {
      checkAvailability(firstItem.roomId, firstItem.fechaIn, firstItem.fechaOut).then(data => {
        setAvailabilityData(data);
      });
    } else {
      setAvailabilityData(null);
    }
  }, [bookingItems]);

  function openView(res) {
    clearErrorAndSuccess();
    setSelected(res);
    setViewDialogOpen(true);
  }

  async function openBookingById(bookingId) {
    if (!bookingId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error('No se encontró la reserva');
      const booking = await response.json();
      openView(booking);
    } catch (err) {
      console.error('Error al cargar reserva:', err);
      setError(err.message || 'Error al cargar reserva');
    } finally {
      setLoading(false);
    }
  }

  function openEdit(res) {
    clearErrorAndSuccess();
    setSelected(res);
    setEditForm({ ...res, checkIn: toISODateOnly(res.checkIn), checkOut: toISODateOnly(res.checkOut) });
    setEditingDialogOpen(true);
  }

  const getEditRoom = () => {
    if (!editForm) return null;
    const roomId = editForm.roomId?._id || editForm.roomId;
    if (!roomId) return null;
    return rooms.find(r => String(r._id) === String(roomId)) || null;
  };

  const getEditNights = () => {
    if (!editForm?.checkIn || !editForm?.checkOut) return 0;
    const inDate = editForm.checkIn.split('-');
    const outDate = editForm.checkOut.split('-');
    const checkIn = new Date(inDate[0], inDate[1] - 1, inDate[2]);
    const checkOut = new Date(outDate[0], outDate[1] - 1, outDate[2]);
    return Math.max(0, Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24)));
  };

  const getEditSuggestedTotal = () => {
    const room = getEditRoom();
    const nights = getEditNights();
    if (!room || nights <= 0) return 0;
    const subtotal = room.price * nights;
    return subtotal + subtotal * 0.16;
  };

  const handleEditRoomChange = (e) => {
    const newRoomId = e.target.value;
    const room = rooms.find(r => String(r._id) === String(newRoomId));
    setEditForm(prev => {
      if (!prev) return prev;
      const nights = getEditNightsForForm(prev);
      const manual = typeof prev.precioManual === 'string' ? prev.precioManual.trim() : '';
      const isItemFree = manual.toLowerCase() === 'gratis';
      const manualNumber = manual && manual.toLowerCase() !== 'gratis' ? parseFloat(manual) : null;
      let totalPrice = prev.totalPrice;
      if (!isItemFree && !manualNumber && room && nights > 0) {
        const subtotal = room.price * nights;
        totalPrice = subtotal + subtotal * 0.16;
      }
      const roomName = room?.name || '';
      return {
        ...prev,
        roomId: newRoomId,
        specificRoomId: '',
        roomName,
        pricePerNight: isItemFree ? 0 : (room?.price || 0),
        totalPrice,
      };
    });
  };

  const getEditNightsForForm = (form) => {
    if (!form?.checkIn || !form?.checkOut) return 0;
    const inDate = form.checkIn.split('-');
    const outDate = form.checkOut.split('-');
    const checkIn = new Date(inDate[0], inDate[1] - 1, inDate[2]);
    const checkOut = new Date(outDate[0], outDate[1] - 1, outDate[2]);
    if (isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) return 0;
    return Math.max(0, Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24)));
  };

  function openSignatureDialog(res) {
    clearErrorAndSuccess();
    setSelected(res);
    setSignatureDialogOpen(true);
    setCheckinStep(1);
    setHasSignaturePolicies(false);
    setSignatureDataURL(null);
    setAdditionalInfo({ ciudad: '', estado: '', includeBreakfast: false, markSecondNightPaid: false });
    setTimeout(() => {
      if (canvasPoliciesRef.current) {
        const ctx = canvasPoliciesRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasPoliciesRef.current.width, canvasPoliciesRef.current.height);
      }
    }, 100);
  }

  function closeSignatureDialog() {
    setSignatureDialogOpen(false);
    setCheckinStep(1);
    setHasSignaturePolicies(false);
    setSignatureDataURL(null);
    setAdditionalInfo({ ciudad: '', estado: '', includeBreakfast: false, markSecondNightPaid: false });
    if (canvasPoliciesRef.current) {
      const ctx = canvasPoliciesRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasPoliciesRef.current.width, canvasPoliciesRef.current.height);
    }
  }

  function clearErrorAndSuccess() {
    setError(null);
    setSuccess(null);
  }

  function canProceedFromStep(step) {
    if (step === 1) return hasSignaturePolicies;
    if (step === 2) return additionalInfo.ciudad.trim() !== '' && additionalInfo.estado.trim() !== '';
    return false;
  }

  function handleNextStep() {
    if (checkinStep === 1) {
      if (canvasPoliciesRef.current) {
        setSignatureDataURL(canvasPoliciesRef.current.toDataURL('image/png'));
      }
    }
    setCheckinStep((s) => s + 1);
  }

  const updateCiudad = useCallback((value) => setAdditionalInfo(prev => ({ ...prev, ciudad: value })), []);
  const updateEstado = useCallback((value) => setAdditionalInfo(prev => ({ ...prev, estado: value })), []);
  const updateIncludeBreakfast = useCallback((checked) => setAdditionalInfo(prev => ({ ...prev, includeBreakfast: checked })), []);
  const updateMarkSecondNightPaid = useCallback((checked) => setAdditionalInfo(prev => ({ ...prev, markSecondNightPaid: checked })), []);
  const clearAdditionalInfo = useCallback(() => setAdditionalInfo({ ciudad: '', estado: '', includeBreakfast: false, markSecondNightPaid: false }), []);

  async function handleSaveEdit() {
    if (!editForm) return;
    if (dayjs(editForm.checkOut).isBefore(dayjs(editForm.checkIn))) {
      setError('Fecha check-out debe ser después de check-in');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const roomId = editForm.roomId?._id || editForm.roomId;
      const payload = { checkIn: editForm.checkIn, checkOut: editForm.checkOut, roomId, roomName: editForm.roomName, guestInfo: editForm.guestInfo, status: editForm.status };
      if (editForm.totalPrice !== undefined) payload.totalPrice = editForm.totalPrice;
      if (editForm.initialPayment !== undefined) payload.initialPayment = editForm.initialPayment;
      if (editForm.secondNightPayment !== undefined) payload.secondNightPayment = editForm.secondNightPayment;
      const response = await fetch(`${API_URL}/api/bookings/${editForm.bookingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar reserva');
      }
      setSuccess('Reserva actualizada exitosamente');
      setEditingDialogOpen(false);
      await fetchReservas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCancelReserva(res) {
    if (!window.confirm('¿Estás seguro de cancelar esta reserva?')) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/${res.bookingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status: 'cancelled' }),
      });
      if (!response.ok) throw new Error('Error al cancelar reserva');
      setSuccess('Reserva cancelada exitosamente');
      await fetchReservas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('No se pudo cancelar la reserva');
    } finally {
      setLoading(false);
    }
  }

  async function handleMarkSecondNightPaid(res) {
    if (!window.confirm('¿Marcar segunda noche como pagada?')) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/${res.bookingId}/mark-paid`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al marcar como pagado');
      setSuccess('Segunda noche marcada como pagada');
      await fetchReservas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('No se pudo marcar como pagado');
    } finally {
      setLoading(false);
    }
  }

  async function handleUnmarkSecondNightPaid(res) {
    if (!window.confirm('¿Desmarcar segunda noche como pagada?')) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/${res.bookingId}/unmark-paid`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error('Error al desmarcar como pagado');
      setSuccess('Segunda noche desmarcada como pagada');
      await fetchReservas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('No se pudo desmarcar como pagado');
    } finally {
      setLoading(false);
    }
  }

  async function handleDownloadVoucher(bookingId) {
    try {
      const response = await fetch(`${API_URL}/api/bookings/download/${bookingId}`);
      if (!response.ok) throw new Error('Error al descargar voucher');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Voucher_${bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess('Voucher descargado exitosamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('No se pudo descargar el voucher');
    }
  }

  async function handleResendVoucher(bookingId, guestEmail) {
    setError(null);
    setSuccess(null);
    setResendingVoucherId(bookingId);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/resend-voucher`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'No se pudo reenviar el voucher');
      setSuccess(`Voucher reenviado a ${guestEmail || 'huésped'} correctamente`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'No se pudo reenviar el voucher');
    } finally {
      setResendingVoucherId(null);
    }
  }

  const handleCopyBookingLink = () => {
    if (!selectedWeddingAssignment?.token) {
      setError('Esta boda no tiene link disponible');
      return;
    }
    navigator.clipboard.writeText(`${window.location.origin}/guest-assignment/${selectedWeddingAssignment.token}`);
    setSuccess('Link copiado');
  };

  async function handleExportWeddingAssignment(assignmentId, eventName) {
    const svgToPngBlob = (svgEl, scale = 2) => new Promise((resolve) => {
      try {
        if (!svgEl) return resolve(null);
        const vb = svgEl.viewBox.baseVal;
        const vbWidth = vb && vb.width ? vb.width : 660;
        const vbHeight = vb && vb.height ? vb.height : 490;

        const clone = svgEl.cloneNode(true);
        clone.setAttribute('width', String(vbWidth));
        clone.setAttribute('height', String(vbHeight));
        clone.removeAttribute('style');

        const serializer = new XMLSerializer();
        let svgString = serializer.serializeToString(clone);
        if (!svgString.match(/^<svg[^>]+xmlns="http/)) {
          svgString = svgString.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
        }
        const svg64 = btoa(unescape(encodeURIComponent(svgString)));
        const imgSrc = 'data:image/svg+xml;base64,' + svg64;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(vbWidth * scale);
            canvas.height = Math.round(vbHeight * scale);
            const ctx = canvas.getContext('2d');
            ctx.setTransform(scale, 0, 0, scale, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, vbWidth, vbHeight);
            canvas.toBlob((blob) => { resolve(blob); }, 'image/png');
          } catch (err) { resolve(null); }
        };
        img.onerror = () => resolve(null);
        img.src = imgSrc;
      } catch (err) { resolve(null); }
    });

    try {
      const casaSvg = document.getElementById('hidden-export-svg-casa-hotel');
      const boutiqueSvg = document.getElementById('hidden-export-svg-hotel-boutique');
      let mapCasaBlob = null;
      let mapBoutiqueBlob = null;
      if (casaSvg) mapCasaBlob = await svgToPngBlob(casaSvg, 2);
      if (boutiqueSvg) mapBoutiqueBlob = await svgToPngBlob(boutiqueSvg, 2);

      await exportAssignmentXlsx(assignmentId, { mapCasaBlob, mapBoutiqueBlob });
      setSuccess(`${eventName} exportado (XLSX)`);
    } catch (err) {
      try {
        await exportAssignment(assignmentId);
        setSuccess(`${eventName} exportado (CSV)`);
      } catch (err2) {
        setError('Error al exportar asignación de boda');
      }
    }
  }

  async function generateCheckinPDF() {
    if (!hasSignaturePolicies && !signatureDataURL) {
      setError('Por favor, firma las políticas antes de generar el documento');
      return;
    }
    if (!additionalInfo.ciudad.trim() || !additionalInfo.estado.trim()) {
      setError('Por favor, completa la ciudad y el estado');
      return;
    }
    setGeneratingCheckin(true);
    setError(null);
    try {
      const signatureBase64 = signatureDataURL || (canvasPoliciesRef.current ? canvasPoliciesRef.current.toDataURL('image/png') : null);
      if (!signatureBase64) throw new Error('No se pudo obtener la firma. Por favor, regresa al paso 1 y firma de nuevo.');
      const token = localStorage.getItem('authToken');
      if (additionalInfo.markSecondNightPaid && selected?.paymentStatus === 'partial' && !selected?.secondNightNotePaid) {
        const markPaidResponse = await fetch(`${API_URL}/api/bookings/${selected.bookingId}/mark-paid`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!markPaidResponse.ok) {
          const errData = await markPaidResponse.json();
          throw new Error(errData.message || 'Error al marcar segunda noche como pagada');
        }
        fetchReservas();
      }
      const response = await fetch(`${API_URL}/api/bookings/generate-checkin/${selected.bookingId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          signature: signatureBase64,
          ciudad: additionalInfo.ciudad.trim(),
          estado: additionalInfo.estado.trim(),
          includeBreakfast: Boolean(additionalInfo.includeBreakfast),
          markSecondNightPaid: Boolean(additionalInfo.markSecondNightPaid),
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al generar check-in');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Checkin_${selected.bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      const msg = additionalInfo.markSecondNightPaid ? 'Check-in PDF generado y segunda noche marcada como pagada' : 'Check-in PDF generado exitosamente';
      setSuccess(msg);
      closeSignatureDialog();
      fetchSignedCheckinsCount();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.message || 'No se pudo generar el check-in');
    } finally {
      setGeneratingCheckin(false);
    }
  }

  async function handleCreateReservation(e) {
    e.preventDefault();
    if (bookingItems.some(item => !item.roomId || !item.fechaIn || !item.fechaOut || calculateNightsForItem(item) <= 0)) {
      setSnack({ open: true, severity: 'error', message: 'Completa los datos de todas las reservas antes de enviar.' });
      return;
    }
    if (!clienteName || !clienteEmail) {
      setSnack({ open: true, severity: 'error', message: 'Complete información del cliente' });
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) {
      setSnack({ open: true, severity: 'error', message: 'No estás autenticado. Por favor, inicia sesión nuevamente.' });
      return;
    }
    setLoading(true);
    try {
      const guestInfo = {
        firstName: clienteName,
        lastName: clienteLastName || '',
        email: clienteEmail,
        phone: clientePhone || '',
        specialRequests: `Reserva creada por ${origin}`,
      };
      const bookingsPayload = bookingItems.map(item => {
        const room = rooms.find(r => r._id === item.roomId);
        const nightsItem = calculateNightsForItem(item);
        const manualText = item.precioManual?.trim();
        const isItemFree = manualText?.toLowerCase() === 'gratis';
        const manualNumber = manualText && manualText.toLowerCase() !== 'gratis' ? parseFloat(manualText) : null;
        const pricePerNight = isItemFree ? 0 : (room?.price || 0);
        const totalPrice = isItemFree ? 0 : (manualNumber && !Number.isNaN(manualNumber) ? manualNumber : calculateItemTotal(item));
        const specificRoom = item.specificRoomId ? assignmentRooms.find(ar => String(ar._id) === String(item.specificRoomId)) : null;
        const roomName = specificRoom ? `${room?.name || ''} - ${specificRoom.name}` : room?.name || '';
        const scopedGuestAssignmentId = bookingContext === 'wedding' ? selectedWeddingAssignmentId : null;
        return {
          roomId: item.roomId, roomName, checkIn: item.fechaIn, checkOut: item.fechaOut, nights: nightsItem,
          pricePerNight, totalPrice, advancePayment: item.anticipo && item.anticipo.trim() !== '' ? parseFloat(item.anticipo) : 0,
          paymentIntentId: null,
          specialRequests: `Reserva creada por ${origin}${specificRoom ? ` | Habitación específica: ${specificRoom.name}` : ''}`,
          isFree: isItemFree, manualPrice: manualText || '',
          guestAssignmentId: scopedGuestAssignmentId,
        };
      });
      const response = await fetch(`${API_URL}/api/bookings/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ guestInfo, bookings: bookingsPayload, origin, guestAssignmentId: bookingContext === 'wedding' ? selectedWeddingAssignmentId : null }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || result.error || 'Error al crear reservas');
      setSnack({ open: true, severity: 'success', message: `✅ Se crearon ${result.bookings?.length || bookingItems.length} reservas. Se envió un solo correo a ${clienteEmail}.` });
      setOpenNew(false);
      await fetchReservas();
      resetForm();
      if (bookingContext === 'wedding' && selectedWeddingAssignmentId && weddingCreateRoomId && result.bookings?.length) {
        const newBooking = result.bookings[0];
        const assignmentRoom = assignmentRooms.find(ar => String(ar.roomId) === String(weddingCreateRoomId));
        const resolvedRoomId = assignmentRoom?.roomRefId || weddingCreateRoomId;
        setAssignRoomAssignments(prev => ({
          ...prev,
          [String(weddingCreateRoomId)]: {
            bookingId: newBooking.bookingId,
            guestName: `${clienteName} ${clienteLastName || ''}`.trim(),
            guestWhatsapp: clientePhone || '',
            checkIn: newBooking.checkIn,
            checkOut: newBooking.checkOut,
            roomName: newBooking.roomName || '',
          },
        }));
        await loadWeddingAssignments(selectedWeddingAssignmentId);
      }
    } catch (err) {
      setSnack({ open: true, severity: 'error', message: err.message || 'Error al crear reserva' });
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setClienteName(''); setClienteLastName(''); setClienteEmail(''); setClientePhone('');
    setHabitacionId(''); setFechaIn(''); setFechaOut('');
    setUseCalendar(false); setRange([null, null]); setAvailabilityData(null);
    setPrecioManual(''); setAnticipo(''); setBookingContext('general');
    setBookingItems([{ roomId: '', fechaIn: '', fechaOut: '', precioManual: '', anticipo: '', isFree: false, manualPrice: '', specificRoomId: '' }]);
    setWeddingCreateRoomId(null);
    setWeddingCreateDates({ startDate: '', endDate: '' });
  }

  const defaultBookingItem = () => ({ roomId: '', fechaIn: '', fechaOut: '', precioManual: '', anticipo: '', isFree: false, manualPrice: '', specificRoomId: '' });

  const calculateNightsForItem = (item) => {
    if (!item.fechaIn || !item.fechaOut) return 0;
    const inDate = item.fechaIn.split('-');
    const outDate = item.fechaOut.split('-');
    const checkIn = new Date(inDate[0], inDate[1] - 1, inDate[2]);
    const checkOut = new Date(outDate[0], outDate[1] - 1, outDate[2]);
    return Math.max(0, Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24)));
  };

  const calculateItemTotal = (item) => {
    const manual = item.precioManual?.trim();
    if (manual) {
      if (manual.toLowerCase() === 'gratis') return 0;
      const parsed = parseFloat(manual);
      if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    }
    if (!item.roomId || calculateNightsForItem(item) <= 0) return 0;
    const room = rooms.find(r => r._id === item.roomId);
    if (!room) return 0;
    const subtotal = room.price * calculateNightsForItem(item);
    return subtotal + subtotal * 0.16;
  };

  const updateBookingItem = (index, field, value) => {
    setBookingItems(prev => prev.map((item, idx) => idx === index ? { ...item, [field]: value } : item));
  };
  const addBookingItem = () => { if (bookingItems.length < 4) setBookingItems(prev => [...prev, defaultBookingItem()]); };
  const removeBookingItem = (index) => { setBookingItems(prev => prev.filter((_, idx) => idx !== index)); };

  function chipColorForStatus(status) {
    if (status === 'cancelled') return 'error';
    if (status === 'active') return 'success';
    return 'default';
  }

  function chipColorForPayment(paymentStatus) {
    if (paymentStatus === 'completed') return 'success';
    if (paymentStatus === 'partial') return 'warning';
    return 'default';
  }

  function formatDate(date) {
    if (!date) return '—';
    return formatDateForDisplay(date);
  }

  function formatDateLong(date) {
    if (!date) return '-';
    try {
      const str = typeof date === 'string' && date.includes('T') ? date.split('T')[0] : date;
      const [year, month, day] = String(str).split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return String(date); }
  }

  function formatCurrency(amount) {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);
  }

  function availabilityLabelForRoom(r) {
    if (!fechaIn || !fechaOut) return `Total: ${r.totalUnits}`;
    if (habitacionId === r._id && availabilityData) return `Disponibles: ${availabilityData.availableUnits}/${r.totalUnits}`;
    return `Total: ${r.totalUnits}`;
  }

  const stats = useMemo(() => ({
    total: filteredReservas.length,
    active: filteredReservas.filter(r => r.status === 'active').length,
    cancelled: filteredReservas.filter(r => r.status === 'cancelled').length,
    completedPayment: filteredReservas.filter(r => r.paymentStatus === 'completed').length,
    partialPayment: filteredReservas.filter(r => r.paymentStatus === 'partial').length,
    // Solo contar ingresos de reservas activas (las canceladas no suman al ingreso)
    totalRevenue: filteredReservas.reduce((sum, r) => sum + ((r.status === 'active') ? (r.totalPrice || 0) : 0), 0),
    pendingSecondNight: filteredReservas.filter(r => r.paymentStatus === 'partial' && !r.secondNightNotePaid).length,
    stripe: filteredReservas.filter(r => !!r.stripePaymentIntentId).length,
    web: filteredReservas.filter(r => !r.stripePaymentIntentId && !(r.specialRequests || '').match(/WHATSAPP|TELEFONO|RECEPCION|BOOKING/)).length,
    booking: filteredReservas.filter(r => (r.specialRequests || '').includes('BOOKING')).length,
  }), [filteredReservas]);

  // ─────────────────────────────────────────────
  // PASO 1: Políticas
  // ─────────────────────────────────────────────
  const StepPoliciesContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="info" icon={<GavelIcon />}>
        <Typography variant="subtitle2" fontWeight="bold">Por favor, lee con atención todas las políticas del hotel.</Typography>
        <Typography variant="caption">Al firmar al final, confirmas haber leído y aceptado la totalidad de las políticas.</Typography>
      </Alert>
      <Paper variant="outlined" sx={{ p: 2, maxHeight: 380, overflowY: 'auto', bgcolor: 'grey.50' }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: 'primary.main' }}>Políticas La Capilla Hotel</Typography>
        <Typography variant="body2" gutterBottom sx={{ mb: 2, fontStyle: 'italic' }}>Un gusto saludarte y esperamos que te encuentres muy bien, gracias por escogernos como destino para realizar tu viaje...</Typography>
        {[
          { title: '1- Check-in / Check-out', items: ['El check-in es a partir de las 15:00 hrs (sujeto a disponibilidad en caso de ingreso anticipado).', 'El check-out es a las 12:00 hrs.', 'Salida tardía: De 12:30 a 17:00 hrs: $500.00 MXN por persona.', 'A partir de las 17:01 hrs: se cobrará una noche completa.', 'Todo late check-out está sujeto a disponibilidad.'] },
          { title: '2- Pagos y cancelaciones', items: ['El total de la reservación deberá liquidarse al momento del check-in.', 'Cancelaciones con al menos 48 horas de anticipación reciben reembolso del 100% (3 a 5 días hábiles).', 'Cancelaciones con menos de 48 horas generan una penalización del 50% del total de la reservación.', 'En temporada alta (Semana Santa, días festivos y fechas especiales), se requiere aviso con al menos 15 días hábiles.', 'Las políticas pueden variar según temporada y serán confirmadas al momento de la reservación.'] },
          { title: '3- Conducta del huésped', items: ['Las habitaciones y espacios cerrados son 100% libres de humo.', 'No se permite ruido excesivo a partir de las 22:00 hrs en habitaciones, pasillos y áreas comunes.', 'No se permite el uso de bocinas o equipos de audio a alto volumen dentro de las habitaciones.', 'No se permite realizar reuniones o fiestas no autorizadas dentro del hotel.', 'No se permite el consumo de sustancias ilegales dentro de las instalaciones.', 'En caso de incumplimiento, el hotel podrá cancelar la estancia sin derecho a reembolso y solicitar el retiro inmediato del huésped.'] },
          { title: '4- Derecho de admisión y permanencia', items: ['La Capilla Hotel se reserva el derecho de admisión y permanencia.', 'En caso de conductas agresivas, faltas de respeto al personal, daños a instalaciones o afectación a otros huéspedes, el hotel podrá cancelar la estancia sin reembolso, solicitar el retiro inmediato y aplicar cargos correspondientes.'] },
          { title: '5- Responsabilidad por daños', items: ['Cualquier daño causado a habitaciones, mobiliario, jardín, piscina, jacuzzi o instalaciones será responsabilidad del titular de la reservación.', 'El huésped se compromete a cubrir el costo total de reparación o reposición.'] },
          { title: '6- Autorización de cargo a tarjeta', items: ['El huésped autoriza expresamente a La Capilla Hotel a realizar cargos a la tarjeta proporcionada como garantía en caso de: daños a instalaciones, servicios adicionales no liquidados, penalizaciones por incumplimiento o limpieza profunda o especializada.'] },
          { title: '7- Uso de instalaciones', items: ['El uso de piscina y jacuzzi es bajo responsabilidad del huésped.', 'No se permite arrojar objetos, alimentos, bebidas o cualquier elemento que dañe los sistemas o instalaciones.', 'El hotel podrá restringir el uso de estas áreas en caso de mal uso.'] },
          { title: '8- Alimentos y bebidas externas', items: ['No se permite el ingreso de alimentos o bebidas externas, salvo autorización expresa del hotel.', 'El incumplimiento podrá generar cargos adicionales o cancelación de la estancia.'] },
          { title: '9- Capacidad y personas adicionales', items: ['Se deberá respetar la capacidad máxima de cada habitación.', 'Se cobrará $500.00 MXN por persona adicional por la noche.'] },
          { title: '10- Mascotas', items: ['No se aceptan mascotas dentro de las instalaciones.'] },
          { title: '11- Responsabilidad sobre pertenencias', items: ['La Capilla Hotel no se hace responsable por robo, pérdida o daño de pertenencias personales.', 'El hotel no se hace responsable por daños a vehículos dentro del estacionamiento.', 'La Capilla Hotel no se hace responsable por objetos olvidados, extraviados o dejados dentro de las instalaciones o habitaciones.', 'En caso de localizar algún objeto, el hotel podrá resguardarlo por un periodo máximo de 15 días naturales. Transcurrido dicho plazo, el hotel podrá disponer de los objetos sin responsabilidad alguna.', 'El envío o recuperación de dichos objetos será responsabilidad del huésped, incluyendo costos de mensajería o traslado.'] },
          { title: '12- Menores de edad', items: ['Los menores de edad deberán estar supervisados en todo momento por un adulto.', 'El hotel no se hace responsable por accidentes derivados de falta de supervisión.'] },
          { title: '13- Servicios adicionales', items: ['Los servicios adicionales requieren supervisión previa.', 'Cancelaciones con menos de 2 horas de anticipación generarán cargo completo.'] },
        ].map((section) => (
          <Box key={section.title} sx={{ mb: 1.5 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ color: 'text.primary' }}>{section.title}</Typography>
            {section.items.map((item, idx) => (
              <Typography key={idx} variant="body2" sx={{ ml: 2, color: 'text.secondary', fontSize: '0.78rem' }}>• {item}</Typography>
            ))}
          </Box>
        ))}
        <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}>
          Declaro haber leído y aceptado las políticas de estancia de La Capilla Hotel, así como mi responsabilidad sobre el cumplimiento de las mismas.
        </Typography>
      </Paper>
      <Divider />
      <Box>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Firma del huésped — Aceptación de políticas</Typography>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          Al firmar confirmas haber leído y aceptado la totalidad de las políticas del hotel (puntos 1 al 13).
          Esta firma se incluirá en la hoja 1 (check-in) y en la hoja 3 (políticas continuación) del PDF.
        </Typography>
        <SignatureCanvas canvasRef={canvasPoliciesRef} onHasSignatureChange={setHasSignaturePolicies} />
        {!hasSignaturePolicies && <Alert severity="warning" sx={{ mt: 1 }}>Debes firmar para continuar al siguiente paso.</Alert>}
      </Box>
    </Box>
  );

  // ─────────────────────────────────────────────
  // PASO 2: Datos de la reserva
  // ─────────────────────────────────────────────
  const StepReservationDataContent = useMemo(() => {
    const showSecondNightOption = selected?.paymentStatus === 'partial' && !selected?.secondNightNotePaid;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {selected && (
          <Alert severity="info">
            <Typography variant="body2"><strong>Reserva:</strong> {selected.bookingId}</Typography>
            <Typography variant="body2"><strong>Huésped:</strong> {selected.guestInfo?.firstName} {selected.guestInfo?.lastName}</Typography>
            <Typography variant="body2"><strong>Habitación:</strong> {selected.roomName}</Typography>
            <Typography variant="body2"><strong>Check-in:</strong> {formatDateLong(selected.checkIn)}</Typography>
            <Typography variant="body2"><strong>Check-out:</strong> {formatDateLong(selected.checkOut)}</Typography>
            <Typography variant="body2"><strong>Total:</strong> {formatCurrency(selected.totalPrice)}</Typography>
            <Typography variant="body2">
              <strong>Estado de pago:</strong>{' '}
              <Chip label={selected.paymentStatus === 'completed' ? 'Completo' : selected.paymentStatus === 'partial' ? 'Parcial' : selected.paymentStatus} size="small" color={chipColorForPayment(selected.paymentStatus)} sx={{ ml: 0.5 }} />
            </Typography>
            {selected.paymentStatus === 'partial' && (
              <>
                <Typography variant="body2" sx={{ mt: 0.5 }}><strong>Anticipo pagado:</strong> {formatCurrency(selected.initialPayment)}</Typography>
                <Typography variant="body2"><strong>Saldo pendiente:</strong> {formatCurrency(selected.secondNightPayment)}</Typography>
              </>
            )}
          </Alert>
        )}
        <Typography variant="subtitle1" fontWeight="bold">Información adicional del huésped</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Ciudad *" value={additionalInfo.ciudad} onChange={(e) => updateCiudad(e.target.value)} required error={!additionalInfo.ciudad.trim()} helperText={!additionalInfo.ciudad.trim() ? 'Campo requerido' : ''} InputProps={{ startAdornment: <InputAdornment position="start"><LocationCityIcon fontSize="small" /></InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Estado *" value={additionalInfo.estado} onChange={(e) => updateEstado(e.target.value)} required error={!additionalInfo.estado.trim()} helperText={!additionalInfo.estado.trim() ? 'Campo requerido' : ''} InputProps={{ startAdornment: <InputAdornment position="start"><MapIcon fontSize="small" /></InputAdornment> }} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={additionalInfo.includeBreakfast} onChange={(e) => updateIncludeBreakfast(e.target.checked)} />}
              label={<Stack direction="row" alignItems="center" spacing={1}><RestaurantIcon fontSize="small" color={additionalInfo.includeBreakfast ? 'primary' : 'action'} /><Typography variant="body1">¿Incluye desayuno?</Typography></Stack>}
            />
          </Grid>
          {showSecondNightOption && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: additionalInfo.markSecondNightPaid ? 'success.50' : 'warning.50', borderColor: additionalInfo.markSecondNightPaid ? 'success.main' : 'warning.main', borderWidth: 1.5 }}>
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <PaymentsIcon color={additionalInfo.markSecondNightPaid ? 'success' : 'warning'} sx={{ mt: 0.3 }} />
                  <Box flex={1}>
                    <Typography variant="subtitle2" fontWeight="bold" color={additionalInfo.markSecondNightPaid ? 'success.dark' : 'warning.dark'}>Saldo pendiente — Segunda noche</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>El huésped tiene un saldo pendiente de <strong>{formatCurrency(selected?.secondNightPayment || 0)}</strong>. ¿El huésped liquida el saldo en este check-in?</Typography>
                    <FormControlLabel
                      control={<Checkbox checked={additionalInfo.markSecondNightPaid} onChange={(e) => updateMarkSecondNightPaid(e.target.checked)} color="success" />}
                      label={<Stack direction="row" alignItems="center" spacing={0.5}><CheckCircleIcon fontSize="small" color={additionalInfo.markSecondNightPaid ? 'success' : 'action'} /><Typography variant="body2" fontWeight={additionalInfo.markSecondNightPaid ? 'bold' : 'normal'}>Sí, marcar saldo como pagado ({formatCurrency(selected?.secondNightPayment || 0)})</Typography></Stack>}
                    />
                    {additionalInfo.markSecondNightPaid && <Alert severity="success" sx={{ mt: 1 }} icon={<CheckCircleIcon />}>Se marcará el pago como <strong>completo</strong> al generar el PDF.</Alert>}
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          )}
          {selected?.paymentStatus === 'partial' && selected?.secondNightNotePaid && (
            <Grid item xs={12}><Alert severity="success" icon={<CheckCircleIcon />}>La segunda noche ya fue marcada como pagada anteriormente.</Alert></Grid>
          )}
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" startIcon={<ClearIcon />} variant="outlined" onClick={clearAdditionalInfo}>Limpiar campos</Button>
          {selected?.paymentStatus === 'partial' && selected?.secondNightNotePaid && (
            <Button size="small" startIcon={<UndoIcon />} variant="outlined" color="warning" onClick={() => handleUnmarkSecondNightPaid(selected)} disabled={loading} sx={{ ml: 1 }}>Desmarcar 2da noche pagada</Button>
          )}
        </Box>
        {(!additionalInfo.ciudad.trim() || !additionalInfo.estado.trim()) && <Alert severity="warning">Completa ciudad y estado para continuar.</Alert>}
      </Box>
    );
  }, [selected, additionalInfo, updateCiudad, updateEstado, updateIncludeBreakfast, updateMarkSecondNightPaid, clearAdditionalInfo]);

  // ─────────────────────────────────────────────
  // PASO 3: Confirmación
  // ─────────────────────────────────────────────
  const StepConfirmContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="success" icon={<DrawIcon />}>
        <Typography variant="subtitle2" fontWeight="bold">Todo listo. Revisa el resumen y genera el PDF de check-in.</Typography>
        <Typography variant="caption">La firma que proporcionaste en las políticas se incluirá en las hojas 1 y 3 del documento.</Typography>
      </Alert>
      {selected && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Resumen del check-in</Typography>
          <Typography variant="body2"><strong>Reserva:</strong> {selected.bookingId}</Typography>
          <Typography variant="body2"><strong>Huésped:</strong> {selected.guestInfo?.firstName} {selected.guestInfo?.lastName}</Typography>
          <Typography variant="body2"><strong>Ciudad:</strong> {additionalInfo.ciudad} &nbsp;|&nbsp; <strong>Estado:</strong> {additionalInfo.estado}</Typography>
          <Typography variant="body2"><strong>Check-in:</strong> {formatDateLong(selected.checkIn)} &nbsp;→&nbsp; <strong>Check-out:</strong> {formatDateLong(selected.checkOut)}</Typography>
          <Typography variant="body2"><strong>Desayuno:</strong> {additionalInfo.includeBreakfast ? 'Incluye' : 'No incluye'}</Typography>
          <Typography variant="body2"><strong>Total:</strong> {formatCurrency(selected.totalPrice)}</Typography>
          {selected.paymentStatus === 'partial' && (
            <>
              <Typography variant="body2"><strong>Anticipo:</strong> {formatCurrency(selected.initialPayment)}</Typography>
              <Typography variant="body2"><strong>Saldo:</strong> {formatCurrency(selected.secondNightPayment)}</Typography>
            </>
          )}
          {additionalInfo.markSecondNightPaid && <Chip label="✓ Saldo liquidado en este check-in" color="success" size="small" sx={{ mt: 1 }} />}
        </Paper>
      )}
      <Box>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Vista previa de la firma registrada</Typography>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Esta firma aparecerá en la hoja 1 (check-in) y en la hoja 3 (políticas continuación) del PDF.</Typography>
        <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider', display: 'inline-block', width: '100%', maxWidth: 500 }}>
          {signatureDataURL ? (
            <img src={signatureDataURL} alt="Firma del huésped" style={{ border: '1px solid #ccc', backgroundColor: 'white', width: '100%', height: 'auto', display: 'block' }} />
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}><Typography variant="caption" color="error">No se encontró la firma. Por favor regresa al paso 1 y firma de nuevo.</Typography></Box>
          )}
        </Paper>
        {signatureDataURL
          ? <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>✓ Firma capturada correctamente</Typography>
          : <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>✗ Firma no disponible — regresa al paso 1</Typography>
        }
      </Box>
    </Box>
  );

  const renderStepContent = useCallback(() => {
    switch (checkinStep) {
      case 1: return <StepPoliciesContent />;
      case 2: return StepReservationDataContent;
      case 3: return <StepConfirmContent />;
      default: return null;
    }
  }, [checkinStep, StepReservationDataContent]);

  function renderReservaTable(title, color, icon, groupsList, emptyLabel) {
    const totalItems = groupsList.reduce((sum, g) => sum + g.items.length, 0);
    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, px: 2, py: 1.5, bgcolor: color + '10', borderRadius: 2, border: '1px solid', borderColor: color + '30' }}>
          <Box sx={{ color, display: 'flex' }}>{icon}</Box>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ color }}>{title}</Typography>
          <Chip label={totalItems} size="small" sx={{ ml: 'auto', bgcolor: color + '20', color, fontWeight: 600, border: `1px solid ${color}50` }} />
        </Box>
        {groupsList.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, width: 80, textAlign: 'center', py: 0.5 }}>Reserva</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, width: 140, textAlign: 'left', py: 0.5 }}>Huésped</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, width: 90, textAlign: 'center', py: 0.5 }}>Habitación</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, width: 100, textAlign: 'center', py: 0.5 }}>Fechas</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, width: 75, textAlign: 'right', py: 0.5 }} align="right">Total</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, width: 80, textAlign: 'center', py: 0.5 }}>Pago</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, width: 80, textAlign: 'center', py: 0.5 }}>Origen</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, width: 70, textAlign: 'center', py: 0.5 }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, width: 180, textAlign: 'center', py: 0.5 }} align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupsList.map(group => {
                  const isExpanded = expandedGroups[group.key] ?? false;
                  const isGrouped = group.items.length > 1;
                  return (
                    <React.Fragment key={group.key}>
                      {isGrouped && (
                        <TableRow sx={{ bgcolor: 'primary.50', borderLeft: '3px solid', borderColor: 'primary.main' }}>
                          <TableCell colSpan={9} sx={{ py: 0.75 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <IconButton size="small" onClick={() => toggleGroup(group.key)} sx={{ transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                  <KeyboardArrowDownIcon fontSize="small" />
                                </IconButton>
                                <Chip label={`${group.items.length} reservas`} size="small" color="primary" variant="outlined" />
                                <Typography variant="caption" fontWeight={600}>{group.guestEmail}</Typography>
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(group.createdAtMs).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
                          <Collapse in={isGrouped ? isExpanded : true} timeout="auto" unmountOnExit>
                            <Table size="small">
                              <TableBody>
                                {group.items.map((r) => {
                                  const rowKey = typeof r._id === 'object' ? r._id?.$oid : r._id;
                                  return (
                                    <TableRow key={rowKey} hover sx={{ '&:hover': { bgcolor: 'action.hover' }, ...(r.status === 'cancelled' && { opacity: 0.6 }) }}>
                                      <TableCell sx={{ width: 80, textAlign: 'center', verticalAlign: 'top', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="caption" fontFamily="monospace" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.68rem' }}>{r._bookingId?.slice(-8).toUpperCase()}</Typography>
                                      </TableCell>

                                      {/* Huésped */}
                                      <TableCell sx={{ width: 140, textAlign: 'left', verticalAlign: 'top', py: 0.5, borderBottom: '1px solid', borderColor: 'divider', wordBreak: 'break-word' }}>
                                        <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1.3, fontSize: '0.72rem' }}>{r.guestInfo?.firstName} {r.guestInfo?.lastName}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.62rem', lineHeight: 1.2, wordBreak: 'break-word' }}>{r.guestInfo?.email}</Typography>
                                        {r.guestInfo?.phone && (<Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem', lineHeight: 1.2, wordBreak: 'break-word' }}>{r.guestInfo.phone}</Typography>)}
                                      </TableCell>

                                      {/* Habitación */}
                                      <TableCell sx={{ width: 90, textAlign: 'center', verticalAlign: 'top', py: 0.5, borderBottom: '1px solid', borderColor: 'divider', wordBreak: 'break-word' }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>{r.roomName}</Typography>
                                      </TableCell>

                                      {/* Fechas */}
                                      <TableCell sx={{ width: 100, textAlign: 'center', verticalAlign: 'top', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="caption" display="block" sx={{ fontSize: '0.68rem' }}>{formatDate(r.checkIn)} → {formatDate(r.checkOut)}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>{r.nights}n</Typography>
                                      </TableCell>

                                      {/* Total */}
                                      <TableCell align="right" sx={{ width: 75, textAlign: 'right', verticalAlign: 'top', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.72rem' }}>{formatCurrency(r.totalPrice)}</Typography>
                                        {r.paymentStatus === 'partial' && r.initialPayment > 0 && (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.62rem' }}>Ant: {formatCurrency(r.initialPayment)}</Typography>
                                        )}
                                      </TableCell>

                                      {/* Pago */}
                                      <TableCell sx={{ width: 80, textAlign: 'center', verticalAlign: 'top', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <PaymentPill paymentStatus={r.paymentStatus} secondNightNotePaid={r.secondNightNotePaid} />
                                      </TableCell>

                                      {/* Origen */}
                                      <TableCell sx={{ width: 80, textAlign: 'center', verticalAlign: 'top', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <Stack spacing={0.3} alignItems="center">
                                          <OriginPill reserva={r} />
                                          {r.discountCode && (
                                            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'primary.main', fontWeight: 600 }}>{r.discountCode}</Typography>
                                          )}
                                        </Stack>
                                      </TableCell>

                                      {/* Estado */}
                                      <TableCell sx={{ width: 70, textAlign: 'center', verticalAlign: 'top', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <StatusPill status={r.status} />
                                      </TableCell>

                                      {/* Acciones */}
                                      <TableCell sx={{ width: 180, textAlign: 'center', verticalAlign: 'top', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, alignItems: 'center' }}>
                                          <Stack direction="row" spacing={0.15} justifyContent="center">
                                            <Tooltip title="Ver"><IconButton size="small" onClick={() => openView(r)}><VisibilityIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                                            <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(r)} disabled={loading}><EditIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                                            <Tooltip title="Voucher"><IconButton size="small" onClick={() => handleDownloadVoucher(r.bookingId)}><DownloadIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                                            <Tooltip title="Reenviar voucher por email">
                                              <IconButton size="small" color="secondary" onClick={() => handleResendVoucher(r.bookingId, r.guestInfo?.email)} disabled={resendingVoucherId === r.bookingId}>
                                                {resendingVoucherId === r.bookingId ? <CircularProgress size={16} /> : <ForwardToInboxIcon sx={{ fontSize: 14 }} />}
                                              </IconButton>
                                            </Tooltip>
                                          </Stack>
                                          <Stack direction="row" spacing={0.15} justifyContent="center">
                                            <Tooltip title="Check-in"><IconButton size="small" color="info" onClick={() => openSignatureDialog(r)}><AssignmentIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                                            {r.status !== 'cancelled' && (
                                              <Tooltip title="Cancelar"><IconButton size="small" color="error" onClick={() => handleCancelReserva(r)} disabled={loading}><CancelIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                                            )}
                                            {(r.paymentStatus === 'partial' && !r.secondNightNotePaid) && (
                                              <Tooltip title="Marcar 2da noche"><IconButton size="small" color="success" onClick={() => handleMarkSecondNightPaid(r)} disabled={loading}><CheckCircleIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                                            )}
                                            {(r.paymentStatus === 'partial' && r.secondNightNotePaid) && (
                                              <Tooltip title="Desmarcar"><IconButton size="small" color="warning" onClick={() => handleUnmarkSecondNightPaid(r)} disabled={loading}><UndoIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                                            )}
                                          </Stack>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  }

  const hasActiveFilters = filters.search || filters.status || filters.paymentStatus || filters.dateRange.start || filters.dateRange.end;

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <ManageAccountsIcon />
        <Typography variant="h6">Panel para personal — Reservas internas</Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Typography sx={{ mb: 2 }}>
        Registrar reservas realizadas por teléfono, WhatsApp, Booking o recepción. El sistema verifica disponibilidad automáticamente.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button startIcon={<WhatsAppIcon />} variant="contained" color="success" onClick={() => { setOrigin('WHATSAPP'); setBookingContext('general'); setOpenNew(true); }}>
          Nueva reserva
        </Button>
        <Button startIcon={<EventSeatIcon />} variant="contained" color="secondary" onClick={() => { setOrigin('BOOKING'); setBookingContext('general'); setOpenNew(true); }}>
          Booking
        </Button>
        <Button startIcon={<CelebrationIcon />} variant="contained" color="info" onClick={() => setWeddingDialogOpen(true)}>
          Bodas
        </Button>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Ordenar</InputLabel>
          <Select label="Ordenar" value={`${sortBy}:${sortDir}`} onChange={(e) => {
            const [by, dir] = String(e.target.value).split(':');
            setSortBy(by);
            setSortDir(dir);
          }}>
            <MenuItem value="createdAt:desc">Creado — más reciente</MenuItem>
            <MenuItem value="createdAt:asc">Creado — más antiguo</MenuItem>
            <MenuItem value="checkIn:asc">Fecha check-in — ascendente</MenuItem>
            <MenuItem value="checkIn:desc">Fecha check-in — descendente</MenuItem>
          </Select>
          <FormHelperText>Ordenar reservas</FormHelperText>
        </FormControl>
      </Box>

      {/* ── STATS ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total reservas', value: stats.total, color: 'primary.main' },
          { label: 'Activas', value: stats.active, color: 'success.main' },
          { label: 'Canceladas', value: stats.cancelled, color: 'error.main' },
          { label: 'Pago completo', value: stats.completedPayment, color: '#2e7d32' },
          { label: 'Con anticipo', value: stats.partialPayment, color: '#e65100' },
          { label: 'Saldo pendiente', value: stats.pendingSecondNight, color: '#b71c1c' },
          { label: 'Vía Stripe', value: stats.stripe, color: '#6772e5' },
          { label: 'Vía Web', value: stats.web, color: '#546e7a' },
          { label: 'Booking', value: stats.booking, color: '#6a1b9a' },
        ].map((s) => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', p: '12px !important' }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: s.color }}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Grid item xs={6} sm={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: '12px !important' }}>
              <Typography variant="h6" fontWeight="bold" color="warning.main">{formatCurrency(stats.totalRevenue)}</Typography>
              <Typography variant="caption" color="text.secondary">Ingreso total</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined" sx={{ cursor: 'pointer', height: '100%' }} onClick={() => setSignedModalOpen(true)}>
            <CardContent sx={{ textAlign: 'center', p: '12px !important' }}>
              <Typography variant="h5" fontWeight="bold" color="primary">{signedCheckinsCount !== null ? signedCheckinsCount : '-'}</Typography>
              <Typography variant="caption" color="text.secondary">Check-ins firmados</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Button fullWidth variant="contained" startIcon={<MapIcon />} onClick={() => setMapModalOpen(true)} sx={{ height: '100%', minHeight: 64 }}>
            Ver mapa
          </Button>
        </Grid>
      </Grid>

      {/* ── FILTROS ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <FilterListIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" color="text.secondary">Filtros</Typography>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar por ID, nombre, email, habitación..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                  endAdornment: filters.search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setFilters(prev => ({ ...prev, search: '' }))}><ClearIcon /></IconButton>
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select value={filters.status} label="Estado" onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="active">Activas</MenuItem>
                  <MenuItem value="cancelled">Canceladas</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Pago</InputLabel>
                <Select value={filters.paymentStatus} label="Pago" onChange={(e) => setFilters(prev => ({ ...prev, paymentStatus: e.target.value }))}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="completed">Completado</MenuItem>
                  <MenuItem value="partial">Parcial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField type="date" label="Desde" value={filters.dateRange.start ? toISODateOnly(filters.dateRange.start) : ''} onChange={(e) => setFilters(prev => ({ ...prev, dateRange: { ...prev.dateRange, start: e.target.value ? dayjs(e.target.value) : null } }))} InputLabelProps={{ shrink: true }} fullWidth size="small" />
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField type="date" label="Hasta" value={filters.dateRange.end ? toISODateOnly(filters.dateRange.end) : ''} onChange={(e) => setFilters(prev => ({ ...prev, dateRange: { ...prev.dateRange, end: e.target.value ? dayjs(e.target.value) : null } }))} InputLabelProps={{ shrink: true }} fullWidth size="small" />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              startIcon={<DownloadIcon />}
              onClick={handleExportHistory}
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.85rem',
                px: 3,
                py: 1.2,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '&:hover': { boxShadow: '0 6px 16px rgba(0,0,0,0.25)', transform: 'translateY(-1px)' },
                transition: 'all 0.2s ease',
              }}
            >
              Descargar historial
            </Button>
            {hasActiveFilters && (
              <Button startIcon={<ClearIcon />} size="small" onClick={clearAllFilters} variant="text">Limpiar filtros</Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {hasSearch ? (
            <>Búsqueda: <strong>{filteredReservas.length}</strong> reservas encontradas</>
          ) : (
            <>Página <strong>{page}</strong> de <strong>{Math.max(1, Math.ceil(total / pageSize))}</strong> · <strong>{total}</strong> reservas en total</>
          )}
          {hasActiveFilters && ' (filtradas)'}
        </Typography>
        {!hasSearch && total > pageSize && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" variant="outlined" disabled={page <= 1} onClick={() => fetchReservas(page - 1)}>Anterior</Button>
            <Typography variant="body2" color="text.secondary">Página {page} de {Math.max(1, Math.ceil(total / pageSize))}</Typography>
            <Button size="small" variant="outlined" disabled={page >= Math.ceil(total / pageSize)} onClick={() => fetchReservas(page + 1)}>Siguiente</Button>
          </Stack>
        )}
      </Box>

      <Dialog open={weddingDialogOpen} onClose={() => setWeddingDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CelebrationIcon color="secondary" />
            <Typography variant="h6">Gestión de bodas</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControl fullWidth size="small">
                  <InputLabel>Boda</InputLabel>
                  <Select
                    value={selectedWeddingAssignmentId}
                    label="Boda"
                    onChange={(e) => handleWeddingSelection(e.target.value)}
                  >
                     {weddingAssignments.length === 0 ? (
                       <MenuItem value="" disabled>No hay bodas registradas</MenuItem>
                     ) : (
                       weddingAssignments.map((w) => (
                         <MenuItem key={w._id} value={w._id}>
                           <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                             <Typography variant="body2" fontWeight={600}>{w.eventName} — {w.brideName}</Typography>
                             {(w.startDate || w.endDate) && (
                               <Typography variant="caption" color="text.secondary">
                                 {formatDateForDisplay(w.startDate)} → {formatDateForDisplay(w.endDate)}
                               </Typography>
                             )}
                           </Box>
                         </MenuItem>
                       ))
                     )}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCreateWeddingOpen(true)}
                  sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
                >
                  Crear boda
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={8}>
              {selectedWeddingAssignment ? (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">{selectedWeddingAssignment.eventName}</Typography>
                      {(selectedWeddingAssignment.startDate || selectedWeddingAssignment.endDate) && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#0277bd', fontWeight: 600, mb: 0.5 }}>
                          {formatDateForDisplay(selectedWeddingAssignment.startDate)} → {formatDateForDisplay(selectedWeddingAssignment.endDate)}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {selectedWeddingAssignment.brideName} • {selectedWeddingAssignment.brideEmail}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {weddingGuestRooms.length} invitados con habitación asignada
                      </Typography>
                      {weddingPaymentLoading ? (
                        <Typography variant="caption" color="text.secondary">Cargando estado de pagos...</Typography>
                      ) : weddingPaymentStatus ? (
                        <Typography variant="caption" color="text.secondary">
                          {weddingPaymentStatus.summary.paidGuests} pagados · {weddingPaymentStatus.summary.unpaidGuests} pendientes · {weddingPaymentStatus.summary.partialPayments} parciales
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">No se pudo cargar el estado de pagos.</Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setCreateWeddingOpen(true)}
                        sx={{ textTransform: 'none' }}
                      >
                        Crear boda
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={openAssignDialog}
                        sx={{ textTransform: 'none' }}
                      >
                        Asignar habitaciones
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<LinkIcon />}
                        onClick={handleCopyBookingLink}
                        sx={{ textTransform: 'none' }}
                      >
                        Copiar link
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleExportWeddingAssignment(selectedWeddingAssignmentId, selectedWeddingAssignment.eventName || 'Boda')}
                      >
                        Exportar boda
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              ) : (
                <Alert severity="info">Selecciona una boda para ver la lista de invitados.</Alert>
              )}
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            {weddingGuestRooms.length === 0 ? (
              <Alert severity="info">No hay invitados con habitación asignada para esta boda.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>Invitado</TableCell>
                      <TableCell>WhatsApp</TableCell>
                      <TableCell>Habitación</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Pagado</TableCell>
                      <TableCell>Pendiente</TableCell>
                      <TableCell>Acción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {weddingGuestRooms.map((room, index) => {
                      const roomPrice = findAssignmentRoomPrice(room);
                      return (
                        <TableRow key={`${room.roomId || room.name}-${index}`} hover>
                          <TableCell>{room.guestName || 'Sin nombre'}</TableCell>
                          <TableCell>{room.guestWhatsapp || '—'}</TableCell>
                          <TableCell>{formatAssignmentRoomLabel(room)}</TableCell>
                          <TableCell>{room.paymentTotal !== null ? formatCurrency(room.paymentTotal) : (roomPrice !== null ? formatCurrency(roomPrice) : '—')}</TableCell>
                          <TableCell>{room.paymentPaid !== null ? formatCurrency(room.paymentPaid) : '—'}</TableCell>
                          <TableCell>{room.paymentPending !== null ? formatCurrency(room.paymentPending) : '—'}</TableCell>
                          <TableCell>
                            {room.booking ? (
                              <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                onClick={() => openBookingById(room.booking.bookingId)}
                              >
                                Ver reserva
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  setOrigin('RECEPCION');
                                  setBookingContext('wedding');
                                  setOpenNew(true);
                                  setClienteName(room.guestName?.split(' ')[0] || '');
                                  setClienteLastName(room.guestName?.split(' ').slice(1).join(' ') || '');
                                  setClientePhone(room.guestWhatsapp || '');
                                  setBookingItems([{ roomId: '', fechaIn: dayjs().format('YYYY-MM-DD'), fechaOut: dayjs().add(1, 'day').format('YYYY-MM-DD'), precioManual: '', anticipo: '', isFree: false, manualPrice: '', specificRoomId: '' }]);
                                }}
                              >
                                Reservar
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWeddingDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createWeddingOpen} onClose={() => setCreateWeddingOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Crear nueva boda</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            <TextField
              label="Nombre del evento *"
              fullWidth
              value={newWedding.eventName}
              onChange={(e) => setNewWedding(p => ({ ...p, eventName: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Nombre del contacto *"
              fullWidth
              value={newWedding.brideName}
              onChange={(e) => setNewWedding(p => ({ ...p, brideName: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Email del contacto *"
              type="email"
              fullWidth
              value={newWedding.brideEmail}
              onChange={(e) => setNewWedding(p => ({ ...p, brideEmail: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Teléfono"
              fullWidth
              value={newWedding.bridePhone}
              onChange={(e) => setNewWedding(p => ({ ...p, bridePhone: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Email del novio (opcional)"
              type="email"
              fullWidth
              value={newWedding.groomEmail}
              onChange={(e) => setNewWedding(p => ({ ...p, groomEmail: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <FormControl fullWidth>
              <InputLabel>Tipo de hotel</InputLabel>
              <Select
                value={newWedding.hotelType}
                label="Tipo de hotel"
                onChange={(e) => setNewWedding(p => ({ ...p, hotelType: e.target.value }))}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">Ambos (Casa Hotel + Boutique)</MenuItem>
                <MenuItem value="casa">Solo Casa Hotel</MenuItem>
                <MenuItem value="boutique">Solo Hotel Boutique</MenuItem>
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fecha inicio de boda"
                  type="date"
                  fullWidth
                  value={newWedding.startDate}
                  onChange={(e) => setNewWedding(p => ({ ...p, startDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fecha fin de boda"
                  type="date"
                  fullWidth
                  value={newWedding.endDate}
                  onChange={(e) => setNewWedding(p => ({ ...p, endDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setCreateWeddingOpen(false)} sx={{ textTransform: 'none', color: '#666' }}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateWeddingAssignment}
            disabled={creatingWedding}
            startIcon={creatingWedding ? <CircularProgress size={16} /> : <SendIcon />}
            sx={{ bgcolor: '#ffffff', '&:hover': { bgcolor: '#afafaf' }, textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 3 }}
          >
            {creatingWedding ? 'Creando...' : 'Crear y enviar email'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Georgia, serif', borderBottom: '1px solid #e0e0e0' }}>
          Asignar habitaciones — {selectedWeddingAssignment?.eventName || ''}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Escribe el nombre del huésped en cada habitación para buscar y asignar su reserva. Las habitaciones asignadas aparecerán en verde.
            </Typography>
          </Box>
          {assignLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : assignError ? (
            <Alert severity="error">{assignError}</Alert>
          ) : assignAllRooms.length === 0 ? (
            <Alert severity="info">Esta boda no tiene habitaciones configuradas.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {['casaHotel', 'boutique'].map((section) => {
                const sectionRooms = assignAllRooms.filter(room => {
                  const lugar = room.lugar || room.roomType?.lugar || (section === 'casaHotel' ? 'casaHotel' : 'boutique');
                  return lugar === section;
                });
                if (sectionRooms.length === 0) return null;

                const sectionTitle = section === 'casaHotel' ? 'Casa Hotel' : 'Hotel Boutique';
                const sectionIcon = section === 'casaHotel' ? <HomeIcon sx={{ color: '#d4af37' }} /> : <HotelIcon sx={{ color: '#d4af37' }} />;

                return (
                  <Box key={section}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pb: 1, borderBottom: '2px solid #d4af37' }}>
                      {sectionIcon}
                      <Typography variant="h6" fontWeight="700" sx={{ fontFamily: 'Georgia, serif', color: '#1a1a1a' }}>
                        {sectionTitle}
                      </Typography>
                      <Chip label={`${sectionRooms.length} habitaciones`} size="small"
                        sx={{ bgcolor: '#f5f5f5', color: '#666', border: '1px solid #e0e0e0' }} />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {sectionRooms.map((room) => {
                        const roomId = String(room.roomId || room._id);
                        const assignment = assignRoomAssignments[roomId] || null;
                        const searchTerm = (assignRoomSearchTerms[roomId] || '').trim().toLowerCase();
                        const matchedBookings = searchTerm
                          ? assignBookings.filter((booking) => {
                              const name = `${booking.guestInfo?.firstName || ''} ${booking.guestInfo?.lastName || ''}`.toLowerCase();
                              const id = String(booking.bookingId || '').toLowerCase();
                              return name.includes(searchTerm) || id.includes(searchTerm);
                            })
                          : [];

                        return (
                          <Card key={roomId} variant="outlined" sx={{
                            border: assignment ? '2px solid #d4af37' : '1px solid #e0e0e0',
                            borderRadius: 2,
                            bgcolor: assignment ? '#fffbf0' : '#fff',
                            transition: 'all 0.2s ease',
                          }}>
                            <CardContent>
                              <Stack spacing={2}>
                                 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                   <Box>
                                     <Typography variant="subtitle1" fontWeight="700" sx={{ color: '#1a1a1a', fontFamily: 'Georgia, serif' }}>
                                       {room.name || room.number}
                                     </Typography>
                                     <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
                                       <Chip label={`#${room.number}`} size="small" sx={{ bgcolor: '#1a1a1a', color: '#fff', fontWeight: '500' }} />
                                       <Chip label={room.bed || ''} size="small" sx={{ bgcolor: '#f5f5f5', color: '#666', border: '1px solid #e0e0e0' }} />
                                       <Chip label={`${room.capacity || '?'} pers.`} size="small" sx={{ bgcolor: '#fff', color: '#d4af37', border: '1px solid #d4af37', fontWeight: '500' }} />
                                       {(selectedWeddingAssignment?.startDate || selectedWeddingAssignment?.endDate) && (
                                         <Chip
                                           label={`${formatDateForDisplay(selectedWeddingAssignment.startDate)} → ${formatDateForDisplay(selectedWeddingAssignment.endDate)}`}
                                           size="small"
                                           sx={{ bgcolor: '#f5f5f5', color: '#0277bd', border: '1px solid #0277bd40', fontWeight: 500 }}
                                         />
                                       )}
                                     </Stack>
                                   </Box>
                                  {assignment && (
                                    <Chip
                                      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                      label="Asignada"
                                      size="small"
                                      sx={{ bgcolor: '#f1fdf1', color: '#2e7d32', border: '1px solid #2e7d3230', fontWeight: 600 }}
                                    />
                                  )}
                                </Box>

                                {assignment ? (
                                  <Box sx={{
                                    p: 2,
                                    borderRadius: 1.5,
                                    bgcolor: '#f1fdf1',
                                    border: '1px solid #2e7d3230',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                  }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="subtitle1" fontWeight="700" sx={{ color: '#2e7d32', mb: 0.5 }}>
                                        {assignment.guestName}
                                      </Typography>
                                      <Stack direction="row" spacing={2} sx={{ mt: 0.5 }} flexWrap="wrap">
                                        <Typography variant="body2" color="text.secondary">
                                          📋 {assignment.roomName}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          📅 {formatDateForDisplay(assignment.checkIn)} → {formatDateForDisplay(assignment.checkOut)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          🆔 {assignment.bookingId}
                                        </Typography>
                                      </Stack>
                                    </Box>
                                    <IconButton size="medium" onClick={() => unassignBookingFromRoom(roomId)} sx={{ color: '#c62828', border: '1px solid #ffcdd2' }}>
                                      <ClearIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                 ) : (
                                  <Stack spacing={1.5}>
                                    <Autocomplete
                                      fullWidth
                                      size="medium"
                                      openOnFocus={false}
                                      inputValue={assignRoomSearchTerms[roomId] || ''}
                                      onInputChange={(e, newValue, reason) => {
                                        if (reason === 'input') updateAssignRoomSearch(roomId, newValue);
                                      }}
                                      options={matchedBookings}
                                      filterOptions={(x) => x}
                                      getOptionLabel={(booking) => `${booking.guestInfo?.firstName || ''} ${booking.guestInfo?.lastName || ''}`.trim()}
                                      noOptionsText={searchTerm ? 'Sin resultados' : 'Escribe un nombre o ID para buscar'}
                                      isOptionEqualToValue={(option, value) => option.bookingId === value.bookingId}
                                      getOptionDisabled={(booking) => getAssignedBookingIds().includes(booking.bookingId)}
                                      onChange={(e, booking) => {
                                        if (booking && !getAssignedBookingIds().includes(booking.bookingId)) {
                                          assignBookingToRoom(roomId, booking);
                                        }
                                      }}
                                      componentsProps={{ popper: { style: { zIndex: 1500 }, placement: 'bottom-start' } }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          placeholder="Escribe el nombre del huésped para buscar su reserva..."
                                          InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                              <>
                                                <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                                                {params.InputProps.startAdornment}
                                              </>
                                            ),
                                          }}
                                        />
                                      )}
                                      ListboxProps={{ sx: { maxHeight: 320, p: 0 } }}
                                      PaperComponent={({ children, ...paperProps }) => (
                                        <Paper
                                          {...paperProps}
                                          elevation={4}
                                          sx={{
                                            borderRadius: 2,
                                            border: '1px solid #e0e0e0',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                            overflow: 'hidden',
                                          }}
                                        >
                                          <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="600">
                                              {matchedBookings.length} reserva{matchedBookings.length !== 1 ? 's' : ''} encontrada{matchedBookings.length !== 1 ? 's' : ''}
                                            </Typography>
                                          </Box>
                                          {children}
                                        </Paper>
                                      )}
                                      renderOption={(props, booking) => {
                                        const optionBookingId = booking.bookingId;
                                        const guestName = `${booking.guestInfo?.firstName || ''} ${booking.guestInfo?.lastName || ''}`.trim();
                                        const guestEmail = booking.guestInfo?.email || '';
                                        const alreadyAssigned = getAssignedBookingIds().includes(optionBookingId);
                                        return (
                                          <Box
                                            component="li"
                                            {...props}
                                            key={optionBookingId}
                                            sx={{
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              alignItems: 'center',
                                              gap: 1,
                                              px: 2,
                                              py: 1.5,
                                              opacity: alreadyAssigned ? 0.5 : 1,
                                              borderBottom: '1px solid #f0f0f0',
                                            }}
                                          >
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                              <Typography variant="body1" fontWeight="700" sx={{ fontSize: '0.95rem' }}>
                                                {guestName || 'Sin nombre'}
                                              </Typography>
                                              <Stack direction="row" spacing={2} sx={{ mt: 0.3 }} flexWrap="wrap">
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                  🆔 {optionBookingId}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                  📅 {formatDateForDisplay(booking.checkIn)} → {formatDateForDisplay(booking.checkOut)}
                                                </Typography>
                                                {guestEmail && (
                                                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                    ✉️ {guestEmail}
                                                  </Typography>
                                                )}
                                              </Stack>
                                            </Box>
                                            {alreadyAssigned && (
                                              <Chip label="Ya asignada" size="small" sx={{ bgcolor: '#ffebee', color: '#c62828', border: '1px solid #ffcdd2', fontSize: '0.7rem', flexShrink: 0 }} />
                                            )}
                                          </Box>
                                        );
                                      }}
                                    />
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<AddCircleOutlineIcon />}
                                      onClick={() => openCreateBookingFromWedding(room)}
                                      sx={{ textTransform: 'none', fontWeight: 600 }}
                                    >
                                      Crear reserva
                                    </Button>
                                  </Stack>
                                )}
                              </Stack>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 2, flexWrap: 'wrap', gap: 1, borderTop: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button onClick={() => setAssignDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
            <Button
              variant="text"
              size="small"
              onClick={() => setAssignRoomAssignments({})}
              sx={{ textTransform: 'none', color: '#c62828' }}
              disabled={Object.keys(assignRoomAssignments).length === 0}
            >
              Limpiar asignaciones
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={`${Object.keys(assignRoomAssignments).length} / ${assignAllRooms.length} asignadas`}
              size="small"
              sx={{ bgcolor: '#fff9e6', color: '#d4af37', fontWeight: '500', border: '1px solid #ffe0b2' }}
            />
            <Button
              variant="contained"
              onClick={handleSaveBookingAssignments}
              disabled={assignSaving || Object.keys(assignRoomAssignments).length === 0}
              startIcon={assignSaving ? <CircularProgress size={16} /> : null}
            >
              {assignSaving ? 'Guardando...' : 'Guardar asignaciones'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ── TABS DE SECCIÓN ── */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: 'Todas', color: 'primary', icon: null },
          { key: 'stripe', label: 'Stripe', color: '#6772e5', icon: <LanguageIcon sx={{ fontSize: 16 }} /> },
          { key: 'internal', label: 'Internas', color: '#0277bd', icon: <LocalHotelIcon sx={{ fontSize: 16 }} /> },
          { key: 'booking', label: 'Booking', color: '#6a1b9a', icon: <EventSeatIcon sx={{ fontSize: 16 }} /> },
          { key: 'weddings', label: 'Bodas', color: '#c2185b', icon: <CelebrationIcon sx={{ fontSize: 16 }} /> },
          { key: 'cancelled', label: 'Canceladas', color: '#c62828', icon: <CancelIcon sx={{ fontSize: 16 }} /> },
        ].map(tab => (
          <Button
            key={tab.key}
            size="small"
            variant={activeTableSection === tab.key ? 'contained' : 'outlined'}
            color={activeTableSection === tab.key ? (tab.key === 'all' ? 'primary' : tab.key === 'cancelled' ? 'error' : 'default') : 'default'}
            onClick={() => handleTabChange(tab.key)}
            startIcon={tab.icon}
            sx={{
              ...(activeTableSection !== tab.key && tab.key !== 'all' && tab.key !== 'cancelled' && { borderColor: tab.color + '50', color: tab.color }),
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
            }}
          >
            {tab.label}
          </Button>
        ))}
      </Box>

      {/* ── TABLAS POR SECCIÓN ── */}
      {loading && !openNew && !viewDialogOpen && !editingDialogOpen && !signatureDialogOpen ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <React.Fragment>
          {filteredReservas.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Stack spacing={1} alignItems="center">
                <FilterListIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary">No se encontraron reservas</Typography>
                {hasActiveFilters && <Button variant="outlined" onClick={clearAllFilters} startIcon={<ClearIcon />} size="small">Limpiar filtros</Button>}
              </Stack>
            </Paper>
          ) : (
             <React.Fragment>
               {activeTableSection === 'all' && renderReservaTable('Todas las reservas', 'primary', null, visibleGroups, 'No hay reservas')}
               {activeTableSection === 'stripe' && renderReservaTable('Reservas desde la página web (Stripe)', '#6772e5', <LanguageIcon />, visibleGroups, 'No hay reservas de página web con Stripe')}
               {activeTableSection === 'internal' && renderReservaTable('Reservas internas (teléfono, WhatsApp, recepción)', '#0277bd', <LocalHotelIcon />, visibleGroups, 'No hay reservas internas')}
               {activeTableSection === 'booking' && renderReservaTable('Reservas Booking', '#6a1b9a', <EventSeatIcon />, visibleGroups, 'No hay reservas de Booking')}
               {activeTableSection === 'weddings' && renderWeddingsSection()}
               {activeTableSection === 'cancelled' && renderReservaTable('Reservas canceladas', '#c62828', <CancelIcon />, visibleGroups, 'No hay reservas canceladas')}
             </React.Fragment>
          )}
        </React.Fragment>
      )}

      {/* Dialog: Crear reserva */}
      <Dialog open={openNew} onClose={() => { setOpenNew(false); resetForm(); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AddCircleOutlineIcon color="primary" />
            <Typography variant="h6">Nueva reserva interna</Typography>
          </Stack>
        </DialogTitle>
        <Box component="form" onSubmit={handleCreateReservation}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Selector de origen */}
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Canal de reserva
              </Typography>
              <Stack direction="row" spacing={1}>
                 {[
                  { value: 'WHATSAPP', label: 'WhatsApp', icon: <WhatsAppIcon fontSize="small" />, color: '#25D366' },
                  { value: 'TELEFONO', label: 'Teléfono', icon: <PhoneIcon fontSize="small" />, color: '#ff6d00' },
                  { value: 'RECEPCION', label: 'Recepción', icon: <LocalHotelIcon fontSize="small" />, color: '#0277bd' },
                  { value: 'BOOKING', label: 'Booking', icon: <EventSeatIcon fontSize="small" />, color: '#6a1b9a' },
                ].map(opt => (
                  <Box
                    key={opt.value}
                    onClick={() => setOrigin(opt.value)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.75,
                      px: 1.5, py: 0.75, borderRadius: 2, cursor: 'pointer',
                      border: '2px solid',
                      borderColor: origin === opt.value ? opt.color : 'divider',
                      bgcolor: origin === opt.value ? opt.color + '12' : 'background.paper',
                      color: origin === opt.value ? opt.color : 'text.secondary',
                      fontWeight: origin === opt.value ? 700 : 400,
                      fontSize: '0.82rem',
                      transition: 'all 0.15s',
                      userSelect: 'none',
                    }}
                  >
                    {opt.icon}
                    {opt.label}
                  </Box>
                ))}
              </Stack>
            </Paper>

            <Stack direction="row" spacing={2}>
              <TextField label="Nombre" value={clienteName} onChange={e => setClienteName(e.target.value)} required fullWidth />
              <TextField label="Apellido" value={clienteLastName} onChange={e => setClienteLastName(e.target.value)} fullWidth />
            </Stack>
            <TextField label="Email" value={clienteEmail} onChange={e => setClienteEmail(e.target.value)} type="email" required fullWidth />
            <TextField label="Teléfono" value={clientePhone} onChange={e => setClientePhone(e.target.value)} fullWidth />

            {bookingItems.map((item, index) => {
              const itemNights = calculateNightsForItem(item);
              const itemTotal = calculateItemTotal(item);
              return (
                <Paper key={index} sx={{ p: 3, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="medium">
                        <InputLabel id={`room-type-label-${index}`} shrink>Tipo de habitación</InputLabel>
                        <Select labelId={`room-type-label-${index}`} value={item.roomId} label="Tipo de habitación" displayEmpty onChange={e => handleRoomTypeChange(index, e.target.value)} required>
                          <MenuItem value="" disabled><em>Selecciona una habitación...</em></MenuItem>
                          {rooms.map(r => (
                            <MenuItem key={r._id} value={r._id}>{r.name} — {r.type} ({availabilityLabelForRoom(r)})</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {item.roomId && getAssignmentRoomsForRoom(item.roomId).length > 0 && (
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>Habitación específica (opcional)</InputLabel>
                          <Select value={item.specificRoomId} label="Habitación específica (opcional)" onChange={e => updateBookingItem(index, 'specificRoomId', e.target.value)}>
                            <MenuItem value="">Ninguna</MenuItem>
                            {getAssignmentRoomsForRoom(item.roomId).map(ar => (
                              <MenuItem key={ar._id} value={ar._id}>{ar.name} {ar.description ? `- ${ar.description}` : ''}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>Elige un número de habitación concreto si deseas especificarlo.</FormHelperText>
                        </FormControl>
                      </Grid>
                    )}
                    <Grid item xs={12} md={6} lg={4}>
                      <TextField label="Check-in" type="date" value={item.fechaIn} onChange={e => updateBookingItem(index, 'fechaIn', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: dayjs().format('YYYY-MM-DD') }} fullWidth required />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                      <TextField label="Check-out" type="date" value={item.fechaOut} onChange={e => updateBookingItem(index, 'fechaOut', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: item.fechaIn ? dayjs(item.fechaIn).add(1, 'day').format('YYYY-MM-DD') : dayjs().add(1, 'day').format('YYYY-MM-DD') }} fullWidth required />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                      <TextField label="Precio Manual (opcional)" fullWidth size="small" value={item.precioManual} onChange={(e) => updateBookingItem(index, 'precioManual', e.target.value)} placeholder="Especificar precio o 'gratis'" helperText={item.precioManual ? (item.precioManual.trim().toLowerCase() === 'gratis' ? 'Reserva gratuita' : 'Usando precio manual') : 'Precio automático'} />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                      <TextField label="Anticipo recibido" fullWidth size="small" value={item.anticipo} onChange={(e) => updateBookingItem(index, 'anticipo', e.target.value)} placeholder="Monto del anticipo" helperText={item.anticipo && parseFloat(item.anticipo) >= itemTotal ? 'PAGO COMPLETO' : 'Dejar vacío si no hay anticipo'} />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                      {bookingItems.length > 1 && (
                        <IconButton color="error" onClick={() => removeBookingItem(index)}><DeleteIcon /></IconButton>
                      )}
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="body2"><strong>Noches:</strong> {itemNights}</Typography>
                    <Typography variant="body2"><strong>Total:</strong> ${itemTotal.toFixed(2)}</Typography>
                  </Box>
                </Paper>
              );
            })}

            <Button type="button" startIcon={<AddCircleOutlineIcon />} onClick={addBookingItem} disabled={bookingItems.length >= 4} sx={{ alignSelf: 'flex-start' }}>
              Agregar otra reserva
            </Button>
            <Typography variant="caption" color="text.secondary">Se enviará un solo correo con todas las reservas creadas.</Typography>

            <FormControlLabel
              control={<Switch checked={useCalendar} onChange={(e) => {
                setUseCalendar(e.target.checked);
                if (e.target.checked) {
                  const today = dayjs(); const tomorrow = today.add(1, 'day');
                  setRange([today, tomorrow]);
                  updateBookingItem(0, 'fechaIn', today.format('YYYY-MM-DD'));
                  updateBookingItem(0, 'fechaOut', tomorrow.format('YYYY-MM-DD'));
                }
              }} />}
              label="Usar calendario visual para la primera reserva"
            />

            {useCalendar && (
              <>
                <Typography variant="subtitle2">Fechas: {bookingItems[0]?.fechaIn} → {bookingItems[0]?.fechaOut || 'Seleccione check-out'}</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                  <DateCalendar
                    value={range[0]}
                    onChange={handleCalendarDayClick}
                    minDate={dayjs()}
                    reduceAnimations
                    slots={{
                      day: (props) => {
                        const unavailable = isDayUnavailable(props.day, bookingItems[0]?.roomId);
                        const inRange = isInRange(props.day, range[0], range[1]);
                        const isStartOrEndDay = isStartOrEnd(props.day, range[0], range[1]);
                        return (
                          <PickersDay {...props} disabled={unavailable} sx={{
                            ...(unavailable && { bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }),
                            ...(inRange && { bgcolor: 'primary.light', color: 'white' }),
                            ...(isStartOrEndDay && { bgcolor: 'primary.main', fontWeight: 'bold' }),
                          }} />
                        );
                      }
                    }}
                  />
                </LocalizationProvider>
              </>
            )}

            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Disponibilidad:</strong></Typography>
              {availabilityData ? (
                <Typography variant="body2" color={availabilityData.isAvailable ? 'success.main' : 'error.main'}>
                  {availabilityData.isAvailable ? `${availabilityData.availableUnits} unidades disponibles` : 'No disponible para esas fechas'}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">Selecciona habitación y fechas para revisar disponibilidad</Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setOpenNew(false); resetForm(); }}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={loading || bookingItems.some(item => !item.roomId || !item.fechaIn || !item.fechaOut || calculateNightsForItem(item) <= 0)} startIcon={loading ? <CircularProgress size={20} /> : null}>
              {loading ? 'Creando...' : 'Crear reservas'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Dialog: Ver detalle */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle de reserva</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>{selected.bookingId}</Typography>
                <Stack direction="row" spacing={1}>
                  <Chip label={selected.status} color={chipColorForStatus(selected.status)} size="small" />
                  <OriginPill reserva={selected} />
                </Stack>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Información del huésped</Typography>
                <Typography variant="body2"><strong>Nombre:</strong> {selected.guestInfo?.firstName} {selected.guestInfo?.lastName}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {selected.guestInfo?.email}</Typography>
                <Typography variant="body2"><strong>Teléfono:</strong> {selected.guestInfo?.phone || 'N/A'}</Typography>
                {selected.guestInfo?.specialRequests && <Typography variant="body2"><strong>Solicitudes especiales:</strong> {selected.guestInfo.specialRequests}</Typography>}
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Detalles de la reserva</Typography>
                <Typography variant="body2"><strong>Habitación:</strong> {selected.roomName}</Typography>
                <Typography variant="body2"><strong>Check-in:</strong> {formatDate(selected.checkIn)}</Typography>
                <Typography variant="body2"><strong>Check-out:</strong> {formatDate(selected.checkOut)}</Typography>
                <Typography variant="body2"><strong>Noches:</strong> {selected.nights}</Typography>
                <Typography variant="body2"><strong>Precio por noche:</strong> {formatCurrency(selected.pricePerNight)}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Información de pago</Typography>
                <Typography variant="body2"><strong>Subtotal:</strong> {formatCurrency(selected.subtotal)}</Typography>
                <Typography variant="body2"><strong>IVA (16%):</strong> {formatCurrency(selected.tax)}</Typography>
                <Typography variant="body2"><strong>Total:</strong> {formatCurrency(selected.totalPrice)}</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}><strong>Pago inicial (50%):</strong> {formatCurrency(selected.initialPayment)}</Typography>
                <Typography variant="body2"><strong>Pendiente (2da noche):</strong> {formatCurrency(selected.secondNightPayment)}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Chip label={`Estado: ${selected.paymentStatus}`} color={chipColorForPayment(selected.paymentStatus)} size="small" />
                  {selected.secondNightNotePaid && <Chip label="2da noche pagada ✓" color="success" size="small" />}
                </Stack>
                {selected.stripePaymentIntentId && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: '#f0f0ff', borderRadius: 1, border: '1px solid #6772e533' }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CreditCardIcon sx={{ fontSize: 14, color: '#6772e5' }} />
                      <Typography variant="caption" sx={{ color: '#6772e5', fontWeight: 600 }}>Pago procesado vía Stripe</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">ID: {selected.stripePaymentIntentId}</Typography>
                  </Box>
                )}
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Información adicional</Typography>
                <Typography variant="body2"><strong>Fecha de creación:</strong> {formatDate(selected.createdAt)}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Cerrar</Button>
          {selected && (
            <>
              <Button variant="outlined" startIcon={<AssignmentIcon />} onClick={() => { setViewDialogOpen(false); openSignatureDialog(selected); }}>Generar Check-in</Button>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => handleDownloadVoucher(selected.bookingId)}>Descargar Voucher</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog: Editar reserva */}
      <Dialog open={editingDialogOpen} onClose={() => setEditingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar reserva</DialogTitle>
        <DialogContent>
          {editForm && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Nombre" value={editForm.guestInfo?.firstName || ''} onChange={e => setEditForm(prev => ({ ...prev, guestInfo: { ...prev.guestInfo, firstName: e.target.value } }))} disabled={loading} />
              <TextField label="Apellido" value={editForm.guestInfo?.lastName || ''} onChange={e => setEditForm(prev => ({ ...prev, guestInfo: { ...prev.guestInfo, lastName: e.target.value } }))} disabled={loading} />
              <TextField label="Email" type="email" value={editForm.guestInfo?.email || ''} onChange={e => setEditForm(prev => ({ ...prev, guestInfo: { ...prev.guestInfo, email: e.target.value } }))} disabled={loading} />
              <TextField label="Teléfono" value={editForm.guestInfo?.phone || ''} onChange={e => setEditForm(prev => ({ ...prev, guestInfo: { ...prev.guestInfo, phone: e.target.value } }))} disabled={loading} />
              <FormControl fullWidth>
                <InputLabel>Habitación</InputLabel>
                <Select value={editForm.roomId?._id || editForm.roomId || ''} label="Habitación" onChange={handleEditRoomChange} disabled={loading}>
                  <MenuItem value="" disabled>Selecciona una habitación...</MenuItem>
                  {rooms.map(r => (
                    <MenuItem key={r._id} value={r._id}>{r.name} — {r.type} ({availabilityLabelForRoom(r)})</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {editForm.roomId && getAssignmentRoomsForRoom(editForm.roomId?._id || editForm.roomId).length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Habitación específica (opcional)</InputLabel>
                  <Select value={editForm.specificRoomId || ''} label="Habitación específica (opcional)" onChange={e => {
                    const newSpecificRoomId = e.target.value;
                    setEditForm(prev => {
                      if (!prev) return prev;
                      const room = rooms.find(r => String(r._id) === String(prev.roomId));
                      const specificRoom = newSpecificRoomId ? assignmentRooms.find(ar => String(ar._id) === String(newSpecificRoomId)) : null;
                      const roomName = specificRoom ? `${room?.name || ''} - ${specificRoom.name}` : room?.name || '';
                      return { ...prev, specificRoomId: newSpecificRoomId, roomName };
                    });
                  }} disabled={loading}>
                    <MenuItem value="">Ninguna</MenuItem>
                    {getAssignmentRoomsForRoom(editForm.roomId?._id || editForm.roomId).map(ar => (
                      <MenuItem key={ar._id} value={ar._id}>{ar.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Stack direction="row" spacing={2}>
                <TextField label="Total (MXN)" type="number" value={editForm.totalPrice ?? ''} onChange={e => setEditForm(prev => ({ ...prev, totalPrice: e.target.value }))} disabled={loading} fullWidth helperText={getEditSuggestedTotal() > 0 && Number(editForm.totalPrice) !== getEditSuggestedTotal() ? `Precio sugerido: ${formatCurrency(getEditSuggestedTotal())}` : ''} />
                <TextField label="Anticipo (MXN)" type="number" value={editForm.initialPayment ?? ''} onChange={e => setEditForm(prev => ({ ...prev, initialPayment: e.target.value }))} disabled={loading} fullWidth />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField label="Check-in" type="date" value={editForm.checkIn} onChange={e => setEditForm(prev => ({ ...prev, checkIn: e.target.value }))} InputLabelProps={{ shrink: true }} disabled={loading} fullWidth />
                <TextField label="Check-out" type="date" value={editForm.checkOut} onChange={e => setEditForm(prev => ({ ...prev, checkOut: e.target.value }))} InputLabelProps={{ shrink: true }} inputProps={{ min: editForm.checkIn ? dayjs(editForm.checkIn).add(1, 'day').format('YYYY-MM-DD') : undefined }} disabled={loading} fullWidth />
              </Stack>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select value={editForm.status} label="Estado" onChange={e => setEditForm(prev => ({ ...prev, status: e.target.value }))} disabled={loading}>
                  <MenuItem value="active">Activa</MenuItem>
                  <MenuItem value="cancelled">Cancelada</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingDialogOpen(false)} disabled={loading}>Cancelar</Button>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />} onClick={handleSaveEdit} disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Check-in con 3 pasos */}
      <Dialog open={signatureDialogOpen} onClose={closeSignatureDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AssignmentIcon />
            <Typography variant="h6">Check-in — {selected?.bookingId}</Typography>
          </Stack>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Stepper activeStep={checkinStep - 1} alternativeLabel>
              {checkinSteps.map((label, idx) => (
                <Step key={label} completed={checkinStep - 1 > idx}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>
          </Box>
        </DialogTitle>
        <DialogContent dividers>{renderStepContent()}</DialogContent>
        <DialogActions>
          <Button onClick={closeSignatureDialog} disabled={generatingCheckin}>Cancelar</Button>
          {checkinStep > 1 && <Button onClick={() => setCheckinStep((s) => s - 1)} variant="outlined" disabled={generatingCheckin}>Atrás</Button>}
          {checkinStep < 3 ? (
            <Button variant="contained" onClick={handleNextStep} disabled={!canProceedFromStep(checkinStep) || generatingCheckin}>Siguiente</Button>
          ) : (
            <Button variant="contained" startIcon={generatingCheckin ? <CircularProgress size={20} /> : <DownloadIcon />} onClick={generateCheckinPDF} disabled={!signatureDataURL || !additionalInfo.ciudad.trim() || !additionalInfo.estado.trim() || generatingCheckin} color="success">
              {generatingCheckin ? 'Generando PDF...' : 'Generar Check-in PDF'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>

      <Box sx={{ position: 'absolute', left: -10000, top: -10000, width: 700, height: 540, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div id="hidden-map-export" style={{ width: 700, height: 540 }}>
          <CasaHotelMap soldRooms={[]} idPrefix="hidden-export" />
          <HotelBoutiqueMap soldRooms={[]} idPrefix="hidden-export" />
        </div>
      </Box>

      <RoomMapModal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        checkIn={filters.dateRange.start ? dayjs(filters.dateRange.start).format('YYYY-MM-DD') : bookingItems[0]?.fechaIn}
        checkOut={filters.dateRange.end ? dayjs(filters.dateRange.end).format('YYYY-MM-DD') : bookingItems[0]?.fechaOut}
      />

      <SignedCheckinsDialog open={signedModalOpen} onClose={() => setSignedModalOpen(false)} />
    </Paper>
  );
}